/**
 * 작성자 : 홍선영
 * 날짜 : 2024.11.21
 * 경로 : 일정관리 캘린더
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';

import { Calendar, dayjsLocalizer, Views, Navigate, ToolbarProps, EventPropGetter, EventWrapperProps, NavigateAction, View, SlotInfo, ViewProps, DateLocalizer } from 'react-big-calendar';
import Month from 'react-big-calendar/lib/Month';
import Agenda from 'react-big-calendar/lib/Agenda';

import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { INIT_END_HMS, INIT_START_HMS, SCHEDULE_TAB_LIST, INIT_SCHEDULE } from '../_constants';
import { useMutation } from '@tanstack/react-query';
import { apiDelete, apiPatch, apiPost } from '../services/_common';
import { useRecoilValue } from 'recoil';
import { IUser, userState } from '../atoms';
import { CalendarStyle, MonthPickerPopupStyle, OverlayStyle, ScheduleShowMorePopupStyle, ToolbarStyle } from '../assets/styles/BigCalendarStyle';
import { SelectBoxSmDropStyle } from '../assets/styles/SelectBoxSmDrop';
import { toast } from 'react-toastify';
import ScheduleSaveModal from './Modal/ScheduleSaveModal';
import { IAuth, IModal, Time } from 'customTypes';
import Portal from './Portal';
import { formatDateType, todayYYYYMMDD } from '../utils/formatDate';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import i18n from '../translation/i18n';
import { appendApprovalStatus } from '../utils/appendApprovalStatus';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/ko'; // 한국어 로케일
import 'dayjs/locale/en'; // 영어 로케일
import ClickableContainer from '../pages/h_cctv/RealtimeCCTV/ClickableContainer';
import TableLamp from './TableLamp';
import { logPost } from '../services/log';
import { isMobile } from '../utils/checkMobile';
import SmallTextLabel from './button/SmallTextLabel';
import { copyContentsToClipboard } from '../utils/copyContentsToClipboard';
import { isImageFile } from '../utils/formatFilePath';
import { PhotoImage, ImagePreview } from './ImagePreview';
import { submitFormToFlutterWithCallback, transformFormData } from '../utils/flutterBridge';
import useBackHistory from '../hooks/useBackHistory';
import ConfirmModal from './Modal/ConfirmModal';
import { PulseLoader } from 'react-spinners';

type CalendarView = View | 'twoWeeks' | 'threeWeeks';
// dayjsLocalizer 설정
const localizer = dayjsLocalizer(dayjs);

// 개인 근태(휴가) 일정을 항상 가장 마지막에 표시하기 위한 정렬 보정
// react-big-calendar의 localizer.sortEvents/daySpan은 이벤트 대신 날짜만 받으므로, 휴가 일정의 start Date 객체로 식별한다
// 공휴일도 smGubun 4(isDayOffEvent)로 생성되므로 제외한다
const isPersonalDayOff = (event: { isDayOffEvent?: boolean; isHolidayEvent?: boolean }) => !!event.isDayOffEvent && !event.isHolidayEvent;
const dayOffStartDates = new WeakSet<Date>();
const isDayOffStart = (date: Date) => dayOffStartDates.has(date);
const rbcLocalizer = localizer as any; // daySpan/sortEvents는 @types에 정의되어 있지 않음
const baseDaySpan = rbcLocalizer.daySpan;
const baseSortEvents = rbcLocalizer.sortEvents;
// 월간 뷰(sortWeekEvents)는 여러 날 일정을 무조건 먼저 배치하므로, 휴가 일정은 기간과 무관하게 1일 일정 그룹으로 분류한다
rbcLocalizer.daySpan = (start: Date, end: Date) => (isDayOffStart(start) ? 1 : baseDaySpan(start, end));
rbcLocalizer.sortEvents = (args: { evtA: { start: Date }; evtB: { start: Date } }) => Number(isDayOffStart(args.evtA.start)) - Number(isDayOffStart(args.evtB.start)) || baseSortEvents(args);

// dayjs 로케일을 한국어로 설정
dayjs.locale('ko');

// dayjs 플러그인 로드
dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Seoul');
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const mobile = isMobile();

// EventInteractionArgs 타입
type EventInteractionArgs<T> = {
  event: T;
  start: Date | string;
  end: Date | string;
  isAllDay: boolean;
};

// 이벤트 타입
type Event = {
  id: number;
  title: string;
  smName: string;
  start: Date;
  end: Date;
  allDay: boolean;
  desc?: string;
  fontColor?: string;
  backColor?: string;
  setAttenders?: any;
  bulletColor?: string;
  complete: boolean;
  travelYn: boolean;
  important: boolean;
  isDayOffEvent: boolean;
  smIdx: string;
  smGubun: string;
  orgRepeatSdate: string;
  orgSdate: string;
  orgStime: string;
  orgFdate: string;
  orgFtime: string;
  orgTitle: string;
  attenders: string[];
  isRepeatSchedule: boolean;
  csCd: string;
  csName: string;
  ccCd: string;
  dCd: string;
  dGubun: string[];
  hsCd: string;
  hsCd1: string;
  hsCd2: string;
  wGubun: string;
  userId?: any;
  address: string;
  latitude: string;
  longitude: string;
  files: IFile[];
  writer: string;
  wDate: string;
  editor: string;
  eDate: string;
  repeatsmIdx: string;
  isPaid: string;
  users: string[];
  sName?: string;
  hName?: string;
  siteManagerName?: string;
  siteManagerPhone?: string;
  siteManagerPartname?: string;
  isHolidayEvent?: boolean;
  approvalStatus?: string | null; // 근태 결재 상태 (PENDING: 결재중, HOLD: 보류, APPROVED: 최종승인, null: 일반 일정)
  atCd?: number; // 근태계 번호 (결재중·보류 근태는 smIdx 없이 atCd만 있음)
};

// PopupType 타입
type PopupType = {
  isOpen: boolean;
  isClosing?: boolean;
  isOpening?: boolean;
  date: Date | null;
  events: Event[] | null;
  position: {
    x: number;
    y: number;
    position: 'top' | 'bottom';
    arrowOffset: number;
  } | null;
};

const lang: any = {
  'ko-KR': {
    week: '주별',
    work_week: 'work_week',
    day: '일별',
    month: '월별',
    agenda: '목록',
    previous: '이전',
    next: '다음',
    today: '오늘',
    showMore: (total: number) => ` + ${total}${mobile ? '' : '개  추가 일정 보기'}`,
    noEventsInRange: '일정이 없습니다.',
  },
  'en-US': {
    week: 'week',
    work_week: 'work_week',
    day: 'day',
    month: 'month',
    agenda: 'list',
    previous: 'previous',
    next: 'next',
    today: 'today',
    showMore: (total: number) => ` + ${total} more`,
    noEventsInRange: 'There are no events in this range.',
  },
};

// 공휴일 날짜(YYYYMMDD) 목록: 툴바처럼 일정 데이터를 직접 받지 않는 컴포넌트에서 공휴일 판단에 사용 (툴바 재마운트 방지를 위해 Context 사용)
const HolidayDatesContext = createContext<Set<string>>(new Set());

const CustomMonthDateHeader = ({ label, date, myEvents, isOffRange }: { label: string; date: Date; myEvents?: Event[]; isOffRange?: boolean }) => {
  const { t } = useTranslation();
  const isSaturday = date.getDay() === 6; // 토요일
  const isSunday = date.getDay() === 0; // 일요일
  const isHoliday = React.useMemo(() => {
    if (!date || !myEvents || myEvents.length === 0) return false;
    const currentDate = dayjs(date).startOf('day');
    return myEvents.some((event: Event) => {
      if (!event.isHolidayEvent) return false;
      return currentDate.isSame(dayjs(event.start).startOf('day'), 'day');
    });
  }, [date, myEvents]);

  return (
    <div
      style={{
        color: isSunday || isHoliday ? 'var(--red)' : isSaturday ? 'var(--blue)' : 'inherit', // 일요일/공휴일은 빨간색(토요일 공휴일 포함), 토요일은 파란색
        opacity: isOffRange ? 0.4 : undefined, // 이전/다음달 미리보기 날짜는 흐리게
      }}
    >
      {t(label)}
    </div>
  );
};

// MyToolbar의 props 타입
interface MyToolbarProps extends Omit<ToolbarProps<Event, object>, 'view'> {
  auth: IAuth;
  view: CalendarView; // 'twoWeeks' / 'threeWeeks' 허용
  onView: (view: CalendarView) => void;
  culture: string;
  handleNewEvent: () => void;
  gubunCheckState: GubunCheckType[];
  setGubunCheckState: Dispatch<SetStateAction<GubunCheckType[]>>;
  isSearchSmGubunAll: boolean;
  hideOptionalToolBar?: boolean;
  refetch: () => void;
  showOnlyMyEvents: boolean;
  setShowOnlyMyEvents: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
}
// 현재 년도 기준으로 선택 가능한 년도 범위 (-10년 ~ +10년)
const years = Array.from({ length: 21 }, (_, i) => new Date().getFullYear() - 10 + i);
// 월 목록
const months = Array.from({ length: 12 }, (_, i) => ({ value: i, label: `${i + 1}월` }));

const MyToolbar: React.FC<MyToolbarProps> = ({
  auth,
  onNavigate,
  onView,
  view,
  date,
  handleNewEvent,
  culture,
  gubunCheckState,
  setGubunCheckState,
  isSearchSmGubunAll,
  hideOptionalToolBar,
  refetch,
  showOnlyMyEvents,
  setShowOnlyMyEvents,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const size = useOutletContext<any>();
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(date.getFullYear());
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [showWeekDropdown, setShowWeekDropdown] = useState(false);
  const weekDropdownRef = useRef<HTMLDivElement>(null);

  const handleNavigate = useCallback(
    (action: NavigateAction, newDate?: Date) => {
      if (typeof action === 'function') return;
      if (action === Navigate.DATE && newDate) {
        // 월 선택에 의한 네비게이션인 경우
        onNavigate(Navigate.DATE, newDate);
        return;
      }
      onNavigate(action); // prev/next/today 버튼에 의한 네비게이션인 경우
    },
    [onNavigate]
  );

  // 년/월 선택 핸들러
  const handleMonthSelect = useCallback(
    (year: number, month: number) => {
      const newDate = new Date(year, month, 1); // 선택한 월의 1일로 새로운 날짜 객체 생성
      handleNavigate(Navigate.DATE, newDate); // 캘린더 이동
      setShowMonthPicker(false);
    },
    [handleNavigate]
  );

  // 외부 클릭 시 팝업 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowMonthPicker(false);
      }
      if (weekDropdownRef.current && !weekDropdownRef.current.contains(event.target as Node)) {
        setShowWeekDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 툴바 날짜 포맷 업데이트
  const formattedDate = useMemo(() => {
    const yyyy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const cultureCode = culture.split('-');
    const stringDate = `${yyyy}-${mm}-${dd}`;

    if (view === Views.MONTH) {
      // 연도와 월 함께 표시
      return cultureCode[0] === 'ko' ? dayjs(stringDate).format('YYYY년 M월') : dayjs(stringDate).format('MMMM YYYY');
    }

    if (view === Views.WEEK) {
      // 현재 주의 시작일(일요일)과 종료일(토요일) 계산
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay()); // 이번 주 일요일

      const endOfWeek = new Date(date);
      endOfWeek.setDate(date.getDate() + (6 - date.getDay())); // 이번 주 토요일

      const sd = `${startOfWeek.getFullYear()}-${startOfWeek.getMonth() + 1}-${startOfWeek.getDate()}`;
      const ed = `${endOfWeek.getFullYear()}-${endOfWeek.getMonth() + 1}-${endOfWeek.getDate()}`;

      const sDayjsDate = cultureCode[0] === 'ko' ? dayjs(sd).format('M월 D일') : dayjs(sd).format('LL');
      const eDayjsDate = cultureCode[0] === 'ko' ? dayjs(ed).format('M월 D일') : dayjs(ed).format('LL');

      return startOfWeek.getFullYear() !== endOfWeek.getFullYear() // 년도가 다른 경우
        ? `${sDayjsDate} ~ ${eDayjsDate}`
        : startOfWeek.getMonth() !== endOfWeek.getMonth() // 월이 다른 경우
        ? cultureCode[0] === 'ko'
          ? `${sDayjsDate} ~ ${dayjs(ed).format('M월 D일')}`
          : `${sDayjsDate} ~ ${dayjs(ed).format('MMMM DD')}`
        : cultureCode[0] === 'ko'
        ? `${sDayjsDate} ~ ${dayjs(ed).format('D일')}`
        : `${sDayjsDate} ~ ${dayjs(ed).format('DD')}`;
    }

    if (view === 'twoWeeks' || view === 'threeWeeks') {
      const weekCount = view === 'twoWeeks' ? 2 : view === 'threeWeeks' ? 3 : 1;

      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay()); // 이번 주 일요일

      const endOfRange = new Date(startOfWeek);
      endOfRange.setDate(startOfWeek.getDate() + 7 * weekCount - 1); // N주 뒤 토요일

      const sd = `${startOfWeek.getFullYear()}-${startOfWeek.getMonth() + 1}-${startOfWeek.getDate()}`;
      const ed = `${endOfRange.getFullYear()}-${endOfRange.getMonth() + 1}-${endOfRange.getDate()}`;

      const sDayjsDate = cultureCode[0] === 'ko' ? dayjs(sd).format('M월 D일') : dayjs(sd).format('LL');
      const eDayjsDate = cultureCode[0] === 'ko' ? dayjs(ed).format('M월 D일') : dayjs(ed).format('LL');

      // 년/월 넘어가는 경우까지 대응
      if (startOfWeek.getFullYear() !== endOfRange.getFullYear()) {
        return `${sDayjsDate} ~ ${eDayjsDate}`;
      }
      if (startOfWeek.getMonth() !== endOfRange.getMonth()) {
        return cultureCode[0] === 'ko' ? `${sDayjsDate} ~ ${dayjs(ed).format('M월 D일')}` : `${sDayjsDate} ~ ${dayjs(ed).format('MMMM DD')}`;
      }
      return cultureCode[0] === 'ko' ? `${sDayjsDate} ~ ${dayjs(ed).format('D일')}` : `${sDayjsDate} ~ ${dayjs(ed).format('DD')}`;
    }

    if (view === Views.AGENDA) {
      return cultureCode[0] === 'ko' ? dayjs(stringDate).format('M월') : dayjs(stringDate).format('YYYY MMMM');
    }

    if (view === Views.DAY) {
      // 일별 뷰는 요일까지 표시 (예: 10월 1일 (목))
      return cultureCode[0] === 'ko' ? dayjs(stringDate).format('M월 D일 (ddd)') : dayjs(stringDate).locale('en').format('LL (ddd)');
    }

    return cultureCode[0] === 'ko' ? dayjs(stringDate).format('M월 D일') : dayjs(stringDate).format('LL');
  }, [date, view, culture]);

  // 일별 뷰 제목 색상: 공휴일/일요일은 빨강, 토요일은 파랑 (토요일이 공휴일이면 빨강)
  const holidayDates = useContext(HolidayDatesContext);
  const titleNode =
    view === Views.DAY ? (
      <span style={{ color: holidayDates.has(dayjs(date).format('YYYYMMDD')) || date.getDay() === 0 ? 'var(--red)' : date.getDay() === 6 ? 'var(--blue)' : undefined }}>{formattedDate}</span>
    ) : (
      formattedDate
    );

  // 일정 구부 탭 클릭 핸들러
  const handleGubunClick = useCallback((el: any) => {
    setGubunCheckState((prev: GubunCheckType[]) => {
      let updatedState = [];
      if (el.v === '0') {
        updatedState = prev.map((gb: GubunCheckType) => ({ ...gb, check: !prev[0].check }));
      } else {
        updatedState = prev.map((gb: GubunCheckType) => (gb.gubun === el.v ? { ...gb, check: !gb.check } : gb));

        const allCheckedExceptZero = updatedState.slice(1).every((gb) => gb.check);
        const allUncheckedExceptZero = updatedState.slice(1).every((gb) => !gb.check);

        if (allCheckedExceptZero) {
          updatedState[0] = { ...updatedState[0], check: true };
        }

        if (allUncheckedExceptZero) {
          updatedState[0] = { ...updatedState[0], check: false };
        }
      }
      return updatedState;
    });
  }, []);

  useEffect(() => {
    if (!isSearchSmGubunAll) setGubunCheckState(INIT_GUBUN_CHECK);
  }, [isSearchSmGubunAll]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <ToolbarStyle className={`${size.innerSize.W <= 1024 ? 'mobile' : ''} ${hideOptionalToolBar ? 'dashboardStyle' : ''}`}>
      {size.innerSize.W <= 1024 ? (
        <>
          <div className={`toolbar center ${hideOptionalToolBar ? 'dashboardStyle' : ''}`}>
            <div className='btn-wrapper'>
              <div className='month-title-wrapper'>
                <button className='arrow' type='button' onClick={() => handleNavigate(Navigate.PREVIOUS)}>
                  <span className='material-symbols-rounded'>chevron_left</span>
                </button>
                <div className='month-title' role='presentation' onClick={() => setShowMonthPicker(true)}>
                  {titleNode}
                  {showMonthPicker && (
                    <MonthPickerPopupStyle ref={datePickerRef}>
                      <button
                        type='button'
                        className='month-picker-close'
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMonthPicker(false);
                        }}
                        aria-label={t('닫기')}
                      >
                        <span className='material-symbols-rounded'>close</span>
                      </button>
                      <div className='year-month-selector'>
                        <div className='year-selector'>
                          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                            {years.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='month-grid'>
                          {months.map((month) => (
                            <button
                              key={month.value}
                              type='button'
                              className={date.getMonth() === month.value ? 'selected' : ''}
                              onClick={(e) => {
                                e.stopPropagation(); // 이벤트 버블링 방지
                                handleMonthSelect(selectedYear, month.value);
                              }}
                            >
                              {t(month.label)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </MonthPickerPopupStyle>
                  )}
                </div>
                <button className='arrow' type='button' onClick={() => handleNavigate(Navigate.NEXT)}>
                  <span className='material-symbols-rounded'>chevron_right</span>
                </button>
                <PulseLoader color='rgb(0, 122, 255)' size='5px' style={{ visibility: isLoading ? 'visible' : 'hidden' }} />
              </div>
              {hideOptionalToolBar && (
                <button type='button' className={`my-events-btn ${showOnlyMyEvents ? 'active' : ''}`} onClick={() => setShowOnlyMyEvents((prev) => !prev)}>
                  {t('내 일정')}
                </button>
              )}
            </div>
            {!hideOptionalToolBar && (
              <div className='toolbar left'>
                <div className='btns-wrapper'>
                  {/* 뷰 선택 드롭다운 - 모바일 (월별/1주/2주/3주/일별/목록) */}
                  <div ref={weekDropdownRef} style={{ position: 'relative' }}>
                    <SelectBoxSmDropStyle isDropdownOpen={showWeekDropdown}>
                      <ul>
                        <li
                          className={`defaultOption ${showWeekDropdown ? 'opened' : ''} ${
                            view === Views.MONTH || view === Views.WEEK || view === 'twoWeeks' || view === 'threeWeeks' || view === Views.DAY || view === Views.AGENDA ? 'active' : ''
                          }`}
                          role='presentation'
                          onClick={() => {
                            setShowWeekDropdown(true);
                          }}
                        >
                          <span>
                            {view === Views.MONTH
                              ? t('월별')
                              : view === Views.WEEK
                              ? t('1주')
                              : view === 'twoWeeks'
                              ? t('2주')
                              : view === 'threeWeeks'
                              ? t('3주')
                              : view === Views.DAY
                              ? t('일별')
                              : view === Views.AGENDA
                              ? t('목록')
                              : t('월별')}
                          </span>
                          <div className='arrow-icon-wrapper' style={{ width: 'fit-content !important' }}>
                            <span className='material-symbols-rounded'>keyboard_arrow_down</span>
                          </div>
                        </li>
                        <div className={`optionBox ${showWeekDropdown ? 'on' : 'off'}`}>
                          <li
                            className={view === Views.MONTH ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === Views.MONTH) {
                                refetch();
                              } else {
                                onView(Views.MONTH);
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('월별')}
                          </li>
                          <li
                            className={view === Views.WEEK ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === Views.WEEK) {
                                refetch();
                              } else {
                                onView(Views.WEEK);
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('1주')}
                          </li>
                          <li
                            className={view === 'twoWeeks' ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === 'twoWeeks') {
                                refetch();
                              } else {
                                onView('twoWeeks');
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('2주')}
                          </li>
                          <li
                            className={view === 'threeWeeks' ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === 'threeWeeks') {
                                refetch();
                              } else {
                                onView('threeWeeks');
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('3주')}
                          </li>
                          <li
                            className={view === Views.DAY ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === Views.DAY) {
                                refetch();
                              } else {
                                onView(Views.DAY);
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('일별')}
                          </li>
                          <li
                            className={view === Views.AGENDA ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === Views.AGENDA) {
                                refetch();
                              } else {
                                onView(Views.AGENDA);
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('목록')}
                          </li>
                        </div>
                      </ul>
                    </SelectBoxSmDropStyle>
                  </div>
                  {auth?.createAuth && (
                    <button type='button' onClick={handleNewEvent}>
                      <span className='material-symbols-rounded add'>add</span>
                    </button>
                  )}
                  <button type='button' className={`my-events-btn ${showOnlyMyEvents ? 'active' : ''}`} onClick={() => setShowOnlyMyEvents((prev) => !prev)}>
                    <span className='material-symbols-rounded'>person</span>
                  </button>
                  <button className='refresh-btn' type='button' onClick={handleRefresh}>
                    <span className='material-symbols-rounded refresh'>refresh</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          {!hideOptionalToolBar && (
            <div className='btn-bottom-wrap'>
              <div className='toolbar right'>
                <div className='gubun-guide-wrap'>
                  {isSearchSmGubunAll
                    ? SCHEDULE_TAB_LIST.map((el) => (
                        <div key={el.v} className='gubun-guide-item' role='presentation' onClick={() => handleGubunClick(el)}>
                          <ClickableContainer
                            onClick={(e: any) => e.preventDefault()} //
                            button={<TableLamp use={gubunCheckState[Number(el.v)].check} color={el.color} />}
                          />
                          <label htmlFor={el.v}>{t(el.cdName)}</label>
                        </div>
                      ))
                    : SCHEDULE_TAB_LIST.map((el) => (
                        <div key={el.v} className='gubun-guide-item'>
                          <span style={{ backgroundColor: el.colorCode }} />
                          <b>{t(el.cdName)}</b>
                        </div>
                      ))}
                </div>
                <div className='btn-wrapper today-btn'>
                  <button type='button' onClick={() => onNavigate(Navigate.TODAY)}>
                    {t('오늘')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {!hideOptionalToolBar && (
            <div className='toolbar left'>
              <div className='btn-wrapper'>
                <button
                  type='button'
                  className={view === Views.MONTH ? 'active' : ''}
                  onClick={() => {
                    if (view === Views.MONTH) {
                      refetch();
                    } else {
                      onView(Views.MONTH);
                    }
                  }}
                >
                  {t('월별')}
                </button>
                {/* 드롭다운으로 1주/2주/3주/일별 선택 */}
                <div ref={weekDropdownRef} style={{ position: 'relative' }}>
                  <SelectBoxSmDropStyle isDropdownOpen={showWeekDropdown}>
                    <ul>
                      <li
                        className={`defaultOption ${showWeekDropdown ? 'opened' : ''} ${view === Views.WEEK || view === 'twoWeeks' || view === 'threeWeeks' || view === Views.DAY ? 'active' : ''}`}
                        role='presentation'
                        onClick={() => {
                          setShowWeekDropdown(true);
                        }}
                      >
                        <span>{view === Views.WEEK ? t('1주') : view === 'twoWeeks' ? t('2주') : view === 'threeWeeks' ? t('3주') : view === Views.DAY ? t('일별') : t('1주')}</span>
                        <div className='arrow-icon-wrapper' style={{ width: 'fit-content !important' }}>
                          <span className='material-symbols-rounded'>keyboard_arrow_down</span>
                        </div>
                      </li>
                      {showWeekDropdown && (
                        <div className={`optionBox ${showWeekDropdown ? 'on' : 'off'}`}>
                          <li
                            className={view === Views.WEEK ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === Views.WEEK) {
                                refetch();
                              } else {
                                onView(Views.WEEK);
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('1주')}
                          </li>
                          <li
                            className={view === 'twoWeeks' ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === 'twoWeeks') {
                                refetch();
                              } else {
                                onView('twoWeeks');
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('2주')}
                          </li>
                          <li
                            className={view === 'threeWeeks' ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === 'threeWeeks') {
                                refetch();
                              } else {
                                onView('threeWeeks');
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('3주')}
                          </li>
                          <li
                            className={view === Views.DAY ? 'selectedOption' : ''}
                            role='presentation'
                            onClick={() => {
                              if (view === Views.DAY) {
                                refetch();
                              } else {
                                onView(Views.DAY);
                              }
                              setShowWeekDropdown(false);
                            }}
                          >
                            {t('일별')}
                          </li>
                        </div>
                      )}
                    </ul>
                  </SelectBoxSmDropStyle>
                </div>

                <button
                  type='button'
                  className={view === Views.AGENDA ? 'active' : ''}
                  onClick={() => {
                    if (view === Views.AGENDA) {
                      refetch();
                    } else {
                      onView(Views.AGENDA);
                    }
                  }}
                >
                  {t('목록')}
                </button>

                <button type='button' onClick={() => onNavigate(Navigate.TODAY)}>
                  {t('오늘')}
                </button>
                <button type='button' className={showOnlyMyEvents ? 'active' : ''} onClick={() => setShowOnlyMyEvents((prev) => !prev)}>
                  {t('내 일정')}
                </button>
              </div>
            </div>
          )}

          <div className='toolbar center'>
            <div className='btn-wrapper'>
              <div className='month-title-wrapper'>
                <button className='arrow' type='button' onClick={() => handleNavigate(Navigate.PREVIOUS)}>
                  <span className='material-symbols-rounded'>chevron_left</span>
                </button>
                <div className='month-title' role='presentation' onClick={() => setShowMonthPicker(true)}>
                  {titleNode}
                  {showMonthPicker && (
                    <MonthPickerPopupStyle ref={datePickerRef}>
                      <button
                        type='button'
                        className='month-picker-close'
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMonthPicker(false);
                        }}
                        aria-label={t('닫기')}
                      >
                        <span className='material-symbols-rounded'>close</span>
                      </button>
                      <div className='year-month-selector'>
                        <div className='year-selector'>
                          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                            {years.map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='month-grid'>
                          {months.map((month) => (
                            <button
                              key={month.value}
                              type='button'
                              className={date.getMonth() === month.value ? 'selected' : ''}
                              onClick={(e) => {
                                e.stopPropagation(); // 이벤트 버블링 방지
                                handleMonthSelect(selectedYear, month.value);
                              }}
                            >
                              {t(month.label)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </MonthPickerPopupStyle>
                  )}
                </div>
                <button className='arrow' type='button' onClick={() => handleNavigate(Navigate.NEXT)}>
                  <span className='material-symbols-rounded'>chevron_right</span>
                </button>
                <PulseLoader color='rgb(0, 122, 255)' size='5px' style={{ visibility: isLoading ? 'visible' : 'hidden' }} />
              </div>
              {hideOptionalToolBar && (
                <button type='button' className={`my-events-btn ${showOnlyMyEvents ? 'active' : ''}`} onClick={() => setShowOnlyMyEvents((prev) => !prev)}>
                  {t('내 일정')}
                </button>
              )}
            </div>
          </div>
          {!hideOptionalToolBar && (
            <div className='toolbar right'>
              <div className='gubun-guide-wrap'>
                {isSearchSmGubunAll
                  ? SCHEDULE_TAB_LIST.map((el) => (
                      <div key={el.v} className='gubun-guide-item' role='presentation' onClick={() => handleGubunClick(el)}>
                        <ClickableContainer
                          onClick={(e: any) => e.preventDefault()} //
                          button={<TableLamp use={gubunCheckState[Number(el.v)].check} color={el.color} />}
                        />
                        <label htmlFor={el.v}>{t(el.cdName)}</label>
                      </div>
                    ))
                  : SCHEDULE_TAB_LIST.map((el) => (
                      <div key={el.v} className='gubun-guide-item'>
                        <span style={{ backgroundColor: el.colorCode }} />
                        <b>{t(el.cdName)}</b>
                      </div>
                    ))}
              </div>
              <div className='btn-wrapper write-btn'>
                <button type='button' onClick={handleNewEvent}>
                  {t('일정 추가')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </ToolbarStyle>
  );
};

// 커스텀 이벤트 표시 컴포넌트 (React.memo로 최적화)
const CustomEvent = React.memo(
  ({ event }: { event: Event }) => {
    // 인라인 스타일 객체를 조건부로 생성하여 불필요한 객체 생성 방지
    const bulletStyle = event?.bulletColor ? { backgroundColor: event.bulletColor } : undefined;
    const titleStyle = event?.complete ? { textDecoration: 'line-through' } : undefined;

    return (
      <div className='custom-event-line'>
        {event?.important && <div className='material-symbols-rounded important-star'>star_rate</div>}
        {bulletStyle && <div className='custom-bullet' style={bulletStyle} />}
        <span style={{ whiteSpace: 'nowrap', ...titleStyle }}>{event.title}</span>
      </div>
    );
  },
  // 커스텀 비교 함수: event와 onSelect가 변경되지 않으면 리렌더링 방지
  (prevProps, nextProps) => {
    const prevEvent = prevProps.event;
    const nextEvent = nextProps.event;

    // 이벤트의 주요 속성만 비교하여 성능 최적화
    return (
      prevEvent.id === nextEvent.id &&
      prevEvent.title === nextEvent.title &&
      prevEvent.important === nextEvent.important &&
      prevEvent.complete === nextEvent.complete &&
      prevEvent.bulletColor === nextEvent.bulletColor &&
      prevEvent.backColor === nextEvent.backColor &&
      prevEvent.fontColor === nextEvent.fontColor
    );
  }
);

CustomEvent.displayName = 'CustomEvent';

// SCHEDULE_TAB_LIST를 Map으로 변환
const SCHEDULE_GUBUN_MAP = new Map(SCHEDULE_TAB_LIST.map((item) => [item.v, item]));

// 커스텀 Agenda Event 컴포넌트
const CustomAgendaEvent = React.memo(
  ({ event: agendaEvent, onSelect }: { event: Event; onSelect: (event: Event) => void }) => {
    // 일정구분명 찾기
    const gubunName = React.useMemo(() => {
      // 공휴일도 근태와 같은 구분(4)으로 생성되므로 따로 「공휴일」로 표시
      if (agendaEvent.isHolidayEvent) return '공휴일';
      const gubunInfo = SCHEDULE_GUBUN_MAP.get(agendaEvent.smGubun);
      return agendaEvent.csName || gubunInfo?.cdName || '';
    }, [agendaEvent.smGubun, agendaEvent.csName, agendaEvent.isHolidayEvent]);

    // 참가자명
    const attendersText = React.useMemo(() => {
      if (!agendaEvent.userId || agendaEvent.userId.length === 0) return '';
      return agendaEvent.userId.map((el: any) => el.userName).join(', ');
    }, [agendaEvent.userId]);

    // 스타일 객체
    const gubunStyle = React.useMemo(
      () => ({
        color: agendaEvent?.fontColor || undefined,
        backgroundColor: agendaEvent?.backColor || undefined,
      }),
      [agendaEvent?.fontColor, agendaEvent?.backColor]
    );

    // 본사/현장/제목 복사 핸들러
    const handleCopyInfo = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const copyText = `${agendaEvent.hName || ''}/${agendaEvent.sName || ''}//${agendaEvent.smName || ''}`;
        if (copyText && copyText !== '///') {
          copyContentsToClipboard(e, copyText);
        }
      },
      [agendaEvent.hName, agendaEvent.sName, agendaEvent.smName]
    );

    return (
      <div className='custom-agenda-event-wrapper' onClick={() => onSelect(agendaEvent)} role='button' tabIndex={0}>
        <div className='custom-event-line'>
          <button type='button' className='copy-icon-btn' onClick={handleCopyInfo} aria-label='본사/현장/제목 복사' title='본사/현장/제목 복사'>
            <span className='material-symbols-rounded'>content_copy</span>
          </button>
          <span className='agenda-gubun' style={gubunStyle}>
            {gubunName}
          </span>
          <div className='agenda-company-site-wrapper'>
            <span className='agenda-company'>{agendaEvent.hName}</span>
            <span className='agenda-site'>{agendaEvent.sName}</span>
          </div>
          <span className='agenda-title'>{appendApprovalStatus(agendaEvent.smName, agendaEvent.approvalStatus)}</span>
          <span className='agenda-attenders'>{attendersText}</span>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    const prevEvent = prevProps.event;
    const nextEvent = nextProps.event;

    // userId 배열 비교 최적화
    const prevUserIdLength = prevEvent.userId?.length || 0;
    const nextUserIdLength = nextEvent.userId?.length || 0;
    let userIdsEqual = prevUserIdLength === nextUserIdLength;
    if (userIdsEqual && prevUserIdLength > 0) {
      // 배열 길이가 같으면 각 요소 비교
      for (let i = 0; i < prevUserIdLength; i += 1) {
        if (prevEvent.userId[i]?.userId !== nextEvent.userId[i]?.userId) {
          userIdsEqual = false;
          break;
        }
      }
    }

    // 이벤트의 주요 속성만 비교
    return (
      prevEvent.id === nextEvent.id &&
      prevEvent.smGubun === nextEvent.smGubun &&
      prevEvent.csName === nextEvent.csName &&
      prevEvent.smName === nextEvent.smName &&
      prevEvent.approvalStatus === nextEvent.approvalStatus &&
      prevEvent.hName === nextEvent.hName &&
      prevEvent.sName === nextEvent.sName &&
      prevEvent.fontColor === nextEvent.fontColor &&
      prevEvent.backColor === nextEvent.backColor &&
      userIdsEqual &&
      prevProps.onSelect === nextProps.onSelect
    );
  }
);

CustomAgendaEvent.displayName = 'CustomAgendaEvent';

// 미참석자 라벨 생성 헬퍼 함수
const createAbsenteesLabel = (): HTMLDivElement => {
  const labelDiv = document.createElement('div');
  Object.assign(labelDiv.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    marginBottom: '0.25rem',
  });

  const labelContainer = document.createElement('div');
  Object.assign(labelContainer.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    color: 'var(--filled-blue-deep)',
    backgroundColor: 'var(--tonal-blue)',
    borderRadius: '5px',
    padding: '0.125rem 0.5rem',
    fontSize: '0.75rem',
  });

  const labelText = document.createElement('span');
  labelText.textContent = '미참석자';
  labelContainer.appendChild(labelText);

  // 물음표 아이콘 추가 (라벨 내부)
  const helpIcon = document.createElement('span');
  helpIcon.className = 'material-symbols-rounded';
  helpIcon.textContent = 'help';
  Object.assign(helpIcon.style, {
    fontSize: '0.75rem',
    color: 'var(--filled-blue-deep)',
    cursor: 'help',
    display: 'flex',
    alignItems: 'center',
    lineHeight: '1',
  });
  helpIcon.setAttribute('title', '기술부 인원 중 해당 일자 출장 일정에 참석자로 지정되지 않은 인원');
  labelContainer.appendChild(helpIcon);
  labelDiv.appendChild(labelContainer);

  return labelDiv;
};

// 커스텀 Agenda Date 컴포넌트
const CustomAgendaDate = React.memo(
  ({ label, userPartList, myEvents, gubunKey, agendaDate }: { label?: string; userPartList?: any; myEvents?: Event[]; gubunKey?: string; agendaDate?: Date }) => {
    const dateRef = React.useRef<HTMLDivElement>(null);
    const processedRef = React.useRef<boolean>(false);

    // MM.DD ddd string형식의 날짜를 날짜객체로 변환해서 오늘여부 판단
    // agendaDate의 연도를 사용하여 올바른 연도로 파싱
    const date = React.useMemo(() => {
      if (!label) return null;
      const parsed = dayjs(label, 'MM.DD ddd');
      if (!parsed.isValid()) return null;
      // agendaDate가 있으면 해당 연도와 월을 사용, 없으면 현재 연도 사용
      const baseDate = agendaDate ? dayjs(agendaDate) : dayjs();
      return parsed.year(baseDate.year()).month(baseDate.month());
    }, [label, agendaDate]);
    const isToday = React.useMemo(() => (date ? date.isSame(dayjs(), 'day') : false), [date]);

    // 토요일/일요일 판단
    const isSaturday = React.useMemo(() => (date ? date.day() === 6 : false), [date]);
    const isSunday = React.useMemo(() => (date ? date.day() === 0 : false), [date]);

    // 공휴일 판단 (isHolidayEvent === true 인 이벤트가 해당 날짜에 정확히 있는지 확인)
    // 공휴일 이벤트는 각 날짜마다 개별적으로 부여되므로, 해당 날짜와 정확히 일치하는지만 확인
    const isHoliday = React.useMemo(() => {
      if (!date || !myEvents || myEvents.length === 0) return false;
      const currentDate = dayjs(date).startOf('day');
      return myEvents.some((event: Event) => {
        if (!event.isHolidayEvent) return false;
        const eventStart = dayjs(event.start).startOf('day');
        return currentDate.isSame(eventStart, 'day');
      });
    }, [date, myEvents]);

    // 부모 td 요소에 백그라운드 컬러 부여 및 각 날짜의 마지막 행에 굵은 라인 추가
    React.useEffect(() => {
      if (!label) {
        return;
      }

      // 이벤트 데이터가 변경되면 다시 계산하도록 processedRef 리셋
      processedRef.current = false;

      // requestIdleCallback을 사용하여 브라우저가 유휴 상태일 때 실행
      const idleCallback = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 1));

      const idleId = idleCallback(
        () => {
          if (!dateRef.current) return;

          const currentTr = dateRef.current.closest('tr') as HTMLTableRowElement | null;
          if (!currentTr) return;

          // 날짜 prefix 미리 계산 (반복 계산 방지)
          const datePrefix = label.split(' ')[0];
          let row: HTMLTableRowElement | null = currentTr;
          let processedCount = 0;
          const MAX_PROCESS = 50; // 최대 처리 개수 제한
          let lastRowOfDate: HTMLTableRowElement | null = null;

          // 현재 행부터 시작해서 다음 날짜가 나올 때까지 모든 행에 백그라운드 적용
          while (row && processedCount < MAX_PROCESS) {
            const dateCell = row.querySelector('.rbc-agenda-date-cell') as HTMLElement | null;
            const dateCellText = dateCell?.textContent?.trim();

            // 날짜 셀이 비어있거나 같은 날짜면 백그라운드 적용
            if (!dateCellText || dateCellText === '' || dateCellText.includes(datePrefix)) {
              if (isToday) {
                row.style.backgroundColor = 'var(--tonal-green)';
              }
              lastRowOfDate = row; // 마지막 행 추적
            } else {
              // 다른 날짜가 나오면 중단
              break;
            }

            // 다음 행으로 이동
            row = row.nextElementSibling as HTMLTableRowElement | null;
            processedCount += 1;
          }

          // 각 날짜의 마지막 행에 굵은 border-bottom 추가 (모바일 제외)
          if (lastRowOfDate && !mobile) {
            lastRowOfDate.style.borderBottom = '2px solid var(--outline)';
          }

          // 날짜 셀 내부에 미참석자 정보 추가 (모바일) 또는 별도 컬럼 추가 (데스크탑)
          if (currentTr && lastRowOfDate) {
            // 날짜 셀 div 찾기
            const dateCellDiv = dateRef.current;
            if (!dateCellDiv) return;

            // 모바일: 날짜 셀 내부에 미참석자 정보 추가
            if (mobile) {
              // 이미 추가된 미참석자 정보가 있으면 제거
              const existingAbsenteesInfo = dateCellDiv.querySelector('.mobile-absentees-info');
              if (existingAbsenteesInfo) {
                existingAbsenteesInfo.remove();
              }

              // 미참석자 정보 계산 및 추가
              if (date) {
                const currentDate = dayjs(date);
                const dayEvents =
                  myEvents?.filter((event: Event) => {
                    const eventStart = dayjs(event.start);
                    const eventEnd = dayjs(event.end);
                    return currentDate.isSameOrAfter(eventStart, 'day') && currentDate.isSameOrBefore(eventEnd, 'day');
                  }) || [];

                const hasTravelEvent = dayEvents.some((event: Event) => event.travelYn === true);

                if (hasTravelEvent) {
                  const techDept = userPartList?.find((group: any) => group.grName === '기술부');

                  if (techDept && techDept.subList) {
                    const attendeeUserIds = new Set<string>();
                    dayEvents.forEach((event: Event) => {
                      if (event.userId && Array.isArray(event.userId)) {
                        event.userId.forEach((user: any) => {
                          if (user?.userId) {
                            attendeeUserIds.add(String(user.userId));
                          }
                        });
                      }
                    });

                    const absentees = techDept.subList.filter((user: any) => {
                      const userKey = String(user?.key || '');
                      return userKey && !attendeeUserIds.has(userKey);
                    });

                    if (absentees.length > 0) {
                      const absenteesInfoDiv = document.createElement('div');
                      absenteesInfoDiv.className = 'mobile-absentees-info';
                      Object.assign(absenteesInfoDiv.style, {
                        marginTop: '0.5rem',
                        paddingTop: '0.5rem',
                        borderTop: '1px solid var(--outline)',
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '0.5rem',
                      });

                      const labelDiv = createAbsenteesLabel();
                      Object.assign(labelDiv.style, {
                        marginBottom: '0',
                        flexShrink: '0',
                      });
                      absenteesInfoDiv.appendChild(labelDiv);

                      const contentDiv = document.createElement('div');
                      contentDiv.textContent = absentees.map((user: any) => user.name).join(', ');
                      Object.assign(contentDiv.style, {
                        flex: '1',
                        minWidth: '0',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      });
                      absenteesInfoDiv.appendChild(contentDiv);

                      dateCellDiv.appendChild(absenteesInfoDiv);
                    }
                  }
                }
              }
            } else {
              // 데스크탑: 별도 컬럼 추가
              // 이미 추가된 셀이 있는지 확인하고, 있으면 제거하여 새로 생성
              const existingAdditionalDateCell = currentTr.querySelector('td.rbc-agenda-date-cell-additional');
              if (existingAdditionalDateCell) {
                existingAdditionalDateCell.remove();
              }

              // 셀이 없거나 제거된 경우 새로 생성
              if (!currentTr.querySelector('td.rbc-agenda-date-cell-additional')) {
                // 타입 가드를 위한 로컬 변수
                const firstRow = currentTr;
                const lastRow = lastRowOfDate;

                // 행 개수 계산: firstRow부터 lastRow까지 실제 행 개수 세기
                let rowCount = 0;
                let currentRow: HTMLTableRowElement | null = firstRow;
                while (currentRow && rowCount < MAX_PROCESS) {
                  rowCount += 1;
                  if (currentRow === lastRow) {
                    break;
                  }
                  currentRow = currentRow.nextElementSibling as HTMLTableRowElement | null;
                }

                // 날짜 셀 td 찾기 (div의 부모 td)
                const dateCellTd = dateRef.current?.closest('td.rbc-agenda-date-cell') as HTMLTableCellElement | null;
                if (!dateCellTd) return;

                // 추가 날짜 셀 td 생성
                const additionalDateCellTd = document.createElement('td');
                additionalDateCellTd.className = 'rbc-agenda-date-cell rbc-agenda-date-cell-additional';
                additionalDateCellTd.setAttribute('rowspan', rowCount.toString());

                // 내부 컨테이너 div 생성
                const additionalDateCellDiv = document.createElement('div');
                additionalDateCellDiv.className = 'rbc-agenda-date-cell';
                additionalDateCellDiv.style.display = 'flex';
                additionalDateCellDiv.style.flexDirection = 'column';
                if (dateRef.current) {
                  additionalDateCellDiv.style.color = dateRef.current.style.color || 'inherit';
                }

                // 빈 contentDiv 생성 헬퍼 함수
                const createEmptyContentDiv = () => {
                  const div = document.createElement('div');
                  div.textContent = '-';
                  return div;
                };

                // 날짜 정보가 없거나 출장 일정이 없으면 빈 셀 반환
                if (!date) {
                  additionalDateCellDiv.appendChild(createEmptyContentDiv());
                  additionalDateCellTd.appendChild(additionalDateCellDiv);
                  currentTr.appendChild(additionalDateCellTd);
                  return;
                }

                // 해당 날짜의 모든 이벤트 필터링 (멀티이벤트 포함)
                const currentDate = dayjs(date);
                const dayEvents =
                  myEvents?.filter((event: Event) => {
                    const eventStart = dayjs(event.start);
                    const eventEnd = dayjs(event.end);
                    // 이벤트가 시작일과 종료일 사이에 있는지 확인 (종일 이벤트 포함)
                    return currentDate.isSameOrAfter(eventStart, 'day') && currentDate.isSameOrBefore(eventEnd, 'day');
                  }) || [];

                // 해당 일자의 이벤트 중 출장 일정(travelYn === true)이 하나라도 있는지 확인
                const hasTravelEvent = dayEvents.some((event: Event) => event.travelYn === true);

                // 출장 일정이 없으면 미참석자 계산하지 않음
                if (!hasTravelEvent) {
                  additionalDateCellDiv.appendChild(createEmptyContentDiv());
                  additionalDateCellTd.appendChild(additionalDateCellDiv);
                  currentTr.appendChild(additionalDateCellTd);
                  return;
                }

                // 미참석자 명단 계산 (기술부 그룹만)
                const techDept = userPartList?.find((group: any) => group.grName === '기술부');

                if (techDept && techDept.subList) {
                  // 모든 참석자 userId 수집 (하루에 여러 이벤트가 있어도 중복 제거)
                  const attendeeUserIds = new Set<string>();
                  dayEvents.forEach((event: Event) => {
                    if (event.userId && Array.isArray(event.userId)) {
                      event.userId.forEach((user: any) => {
                        // event.userId는 { userId: string, userName: string } 형태
                        if (user?.userId) {
                          attendeeUserIds.add(String(user.userId));
                        }
                      });
                    }
                  });

                  // 기술부 subList에서 미참석자 필터링
                  // user.key와 attendeeUserIds를 비교하여 참석자가 아닌 사람만 필터링
                  const absentees = techDept.subList.filter((user: any) => {
                    const userKey = String(user?.key || '');
                    return userKey && !attendeeUserIds.has(userKey);
                  });

                  // 라벨 추가
                  additionalDateCellDiv.appendChild(createAbsenteesLabel());

                  // 미참석자 이름 표시
                  const contentDiv = document.createElement('div');
                  contentDiv.textContent = absentees.length > 0 ? absentees.map((user: any) => user.name).join(', ') : '-';
                  additionalDateCellDiv.appendChild(contentDiv);
                } else {
                  additionalDateCellDiv.appendChild(createEmptyContentDiv());
                }

                additionalDateCellTd.appendChild(additionalDateCellDiv);
                // 테이블 행의 맨 끝(우측)에 추가
                currentTr.appendChild(additionalDateCellTd);
              }
            }
          }

          processedRef.current = true;
        },
        { timeout: 1000 }
      );

      // eslint-disable-next-line consistent-return
      return () => {
        if (typeof idleId === 'number') {
          if (window.cancelIdleCallback) {
            window.cancelIdleCallback(idleId);
          } else {
            clearTimeout(idleId as unknown as NodeJS.Timeout);
          }
        }
        // cleanup 시 processedRef 리셋하여 다음 렌더링에서 다시 계산되도록 함
        processedRef.current = false;
      };
    }, [isToday, label, myEvents, userPartList, gubunKey]);

    return (
      <div
        ref={dateRef}
        className='rbc-agenda-date-cell'
        style={{
          color: isSunday || isHoliday ? 'var(--red)' : isSaturday ? 'var(--blue)' : 'inherit', // 토요일 공휴일 포함 공휴일은 빨간색
        }}
      >
        {label}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // label, myEvents, userPartList, gubunKey, agendaDate가 모두 같으면 리렌더링 방지
    return (
      prevProps.label === nextProps.label &&
      prevProps.myEvents === nextProps.myEvents &&
      prevProps.userPartList === nextProps.userPartList &&
      prevProps.gubunKey === nextProps.gubunKey &&
      prevProps.agendaDate?.getTime() === nextProps.agendaDate?.getTime()
    );
  }
);

CustomAgendaDate.displayName = 'CustomAgendaDate';

const AgendaDateWrapper = React.memo(
  ({ label, userPartList, myEvents, gubunKey, agendaDate }: { label?: string; userPartList?: any; myEvents?: Event[]; gubunKey?: string; agendaDate?: Date }) => (
    <CustomAgendaDate label={label} userPartList={userPartList} myEvents={myEvents} gubunKey={gubunKey} agendaDate={agendaDate} />
  ),
  (prevProps, nextProps) =>
    prevProps.label === nextProps.label &&
    prevProps.userPartList === nextProps.userPartList &&
    prevProps.myEvents === nextProps.myEvents &&
    prevProps.gubunKey === nextProps.gubunKey &&
    prevProps.agendaDate?.getTime() === nextProps.agendaDate?.getTime()
);

AgendaDateWrapper.displayName = 'AgendaDateWrapper';

// 커스텀 Agenda Time 컴포넌트
const CustomAgendaTime = React.memo(
  ({ event }: { event?: Event }) => {
    const { t } = useTranslation();
    const cultureCode = i18n.language.split('-');
    if (!event) return null;
    const timeStr = event.allDay
      ? t('종일')
      : `${localizer.format(event.start, 'HH:mm', cultureCode[0] === 'ko' ? 'ko' : 'en')} - ${localizer.format(event.end, 'HH:mm', cultureCode[0] === 'ko' ? 'ko' : 'en')}`;
    return <div className='rbc-agenda-time-cell'>{timeStr}</div>;
  },
  (prevProps, nextProps) => {
    // 이벤트가 변경되지 않았으면 리렌더링 방지
    if (!prevProps.event && !nextProps.event) return true;
    if (!prevProps.event || !nextProps.event) return false;
    return (
      prevProps.event.id === nextProps.event.id &&
      prevProps.event.start.getTime() === nextProps.event.start.getTime() &&
      prevProps.event.end.getTime() === nextProps.event.end.getTime() &&
      prevProps.event.allDay === nextProps.event.allDay
    );
  }
);

CustomAgendaTime.displayName = 'CustomAgendaTime';

const CustomShowMore = (props: any) => {
  const { handleClickShowMore, slotDate, events, count } = props;
  return (
    <div
      className='rbc-show-more'
      role='button'
      tabIndex={0}
      style={{ pointerEvents: mobile ? 'none' : 'auto' }}
      onClick={() => handleClickShowMore(events, slotDate)}
      aria-label='추가 일정 보기'
    >{`+ ${count || 0}${mobile ? '' : '개 추가 일정 보기'}`}</div>
  );
};

// EventWrapper 컴포넌트: 식별자(data-event-id) 부여로 드래그 시 DOM 탐색 최적화
const EventWrapper = ({ event, children }: PropsWithChildren<EventWrapperProps<Event>>) => {
  // 모바일에서 터치 이벤트 전파 방지 (날짜 셀 터치 시 이미지 뷰어가 열리는 문제 해결)
  // react-big-calendar의 월별 뷰에서 일정이 날짜 셀의 대부분을 차지할 때,
  // 날짜 셀을 터치해도 일정 내부의 PhotoView 등이 터치 이벤트를 받아 이미지 뷰어가 열리는 것을 방지
  const handleTouchStart = (e: React.TouchEvent) => {
    if (mobile) {
      e.stopPropagation();
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (mobile) {
      e.stopPropagation();
    }
  };

  return (
    <div
      data-event-id={event?.smIdx || event?.id}
      data-event-smidx={event?.smIdx}
      style={{
        overflow: 'hidden',
      }}
      role='presentation'
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      {children}
    </div>
  );
};

// BigCalendar props 타입 정의
interface BigCalendarProps {
  events: Event[];
  refetch: () => void;
  queryClient: any;
  currentDate: Date;
  setCurrentDate: Dispatch<SetStateAction<Date>>;
  onViewChange?: (view: CalendarView) => void;
  auth: IAuth;
  isSearchSmGubunAll: boolean;
  headInfoList: any;
  scheduleInfoList: any;
  userPartList: any;
  carInfoList: any;
  companyUser: any;
  hideOptionalToolBar: boolean;
  isLoading?: boolean;
}

type GubunCheckType = { gubun: string; check: boolean };
const INIT_GUBUN_CHECK = [
  { gubun: '0', check: true },
  { gubun: '1', check: true },
  { gubun: '2', check: true },
  { gubun: '3', check: true },
  { gubun: '4', check: true },
  { gubun: '5', check: true },
];

/**
 * Month 뷰를 재사용하되,
 * week row를 N개만 렌더링하도록 감싼 래퍼.
 * Month가 받는 range(14/21일)를 주 단위로 묶어서 slice(N)만 표시
 */

type MultiWeekOptions = { localizer: DateLocalizer };

const getLimitedVisibleDays = (date: Date, dateLocalizer: DateLocalizer, weeksToShow: number) => {
  const start = (dateLocalizer as any).startOf?.(date, 'week') ?? date;
  const days = weeksToShow * 7;
  const range: Date[] = [];
  for (let i = 0; i < days; i += 1) {
    range.push(dateLocalizer.add(start, i, 'day'));
  }
  return range;
};

export function createLimitedWeeksMonthView(weeksToShow: number) {
  const LimitedWeeksMonthView: React.FC<ViewProps> & {
    range: (date: Date, options: MultiWeekOptions) => Date[];
    navigate: (date: Date, action: NavigateAction, options: MultiWeekOptions) => Date;
    title: (date: Date, options: MultiWeekOptions) => string;
  } = (props) => {
    const { localizer: viewLocalizer, date } = props as any;
    const limitedLocalizer = useMemo(() => {
      return {
        ...viewLocalizer,
        visibleDays: (targetDate: Date) => getLimitedVisibleDays(targetDate, viewLocalizer as DateLocalizer, weeksToShow),
      };
    }, [viewLocalizer, weeksToShow]);

    return <Month {...props} localizer={limitedLocalizer} date={date} />;
  };

  LimitedWeeksMonthView.range = (date: Date, { localizer: dateLocalizer }: MultiWeekOptions) => {
    return getLimitedVisibleDays(date, dateLocalizer, weeksToShow);
  };

  LimitedWeeksMonthView.navigate = (date: Date, action: NavigateAction, { localizer: dateLocalizer }: MultiWeekOptions) => {
    switch (action) {
      case Navigate.PREVIOUS:
        return dateLocalizer.add(date, -weeksToShow, 'week');
      case Navigate.NEXT:
        return dateLocalizer.add(date, weeksToShow, 'week');
      case Navigate.TODAY:
        return new Date();
      default:
        return date;
    }
  };

  LimitedWeeksMonthView.title = (date: Date, { localizer: dateLocalizer }: MultiWeekOptions) => {
    const limitedDays = getLimitedVisibleDays(date, dateLocalizer, weeksToShow);
    const first = limitedDays[0];
    const last = limitedDays[limitedDays.length - 1];
    return `${dateLocalizer.format(first, 'dayHeaderFormat')} - ${dateLocalizer.format(last, 'dayHeaderFormat')}`;
  };

  return LimitedWeeksMonthView;
}

export const TwoWeeksMonthView = createLimitedWeeksMonthView(2);
export const ThreeWeeksMonthView = createLimitedWeeksMonthView(3);

// 목록(Agenda) 뷰: 기본 Agenda는 시작 시각으로만 정렬하여 여러 날 휴가 일정이 다른 일정보다 위에 표시된다
// Agenda 내부의 filter 결과도 이 클래스로 생성되므로(Symbol.species), 이어지는 sort에서 휴가 일정을 항상 마지막으로 보낸다
class DayOffLastEvents extends Array<Event> {
  sort(compareFn?: (a: Event, b: Event) => number) {
    return super.sort((a, b) => Number(isPersonalDayOff(a)) - Number(isPersonalDayOff(b)) || (compareFn ? compareFn(a, b) : 0));
  }
}

const SortedAgendaView = ({ events: agendaEvents, ...rest }: any) => {
  const events = useMemo(() => DayOffLastEvents.from<Event>(agendaEvents || []), [agendaEvents]);
  return <Agenda {...rest} events={events} />;
};
SortedAgendaView.range = Agenda.range;
SortedAgendaView.navigate = Agenda.navigate;
SortedAgendaView.title = Agenda.title;

// HOC를 사용하여 Toolbar 컴포넌트 생성 (메모이제이션 적용)
const useCreateToolbarWithProps = (
  auth: IAuth,
  culture: string,
  handleNewEvent: () => void,
  gubunCheckState: GubunCheckType[],
  setGubunCheckState: Dispatch<SetStateAction<GubunCheckType[]>>,
  isSearchSmGubunAll: boolean,
  hideOptionalToolBar: boolean,
  refetch: () => void,
  showOnlyMyEvents: boolean,
  setShowOnlyMyEvents: Dispatch<SetStateAction<boolean>>,
  onView: (view: CalendarView) => void,
  isLoading: boolean
) => {
  return useMemo(() => {
    const ToolbarWithProps: React.FC<ToolbarProps<Event, object>> = (props) => {
      const { view } = props;
      const enhancedProps: MyToolbarProps = {
        ...props,
        auth,
        view: view as CalendarView,
        onView: onView as (view: CalendarView) => void,
        culture,
        handleNewEvent,
        gubunCheckState,
        setGubunCheckState,
        isSearchSmGubunAll,
        hideOptionalToolBar,
        refetch,
        showOnlyMyEvents,
        setShowOnlyMyEvents,
        isLoading,
      };
      return <MyToolbar {...enhancedProps} />;
    };

    ToolbarWithProps.displayName = 'ToolbarWithProps';
    return ToolbarWithProps;
  }, [auth, culture, handleNewEvent, gubunCheckState, setGubunCheckState, isSearchSmGubunAll, hideOptionalToolBar, refetch, showOnlyMyEvents, setShowOnlyMyEvents, isLoading]);
};

interface IAddress {
  address1: string;
  postNum: string;
  latitude: string;
  longitude: string;
}

const INIT_HMS = { h: 23, m: 59, s: 0 };

// 뷰 목록
const VALID_VIEWS: CalendarView[] = [Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA, 'twoWeeks', 'threeWeeks'];

// localStorage에서 저장된 뷰 읽기
const getInitialView = (hideOptionalToolBar: boolean): CalendarView => {
  if (hideOptionalToolBar) return Views.MONTH;
  // 모바일인 경우 항상 month view로 시작
  if (mobile) return Views.MONTH;
  if (typeof window === 'undefined') return Views.MONTH;

  const savedView = localStorage.getItem('calendar-view') as CalendarView | null;
  if (savedView && VALID_VIEWS.includes(savedView)) {
    return savedView;
  }
  return Views.MONTH;
};

// 썸네일 이미지 컴포넌트
export const ThumbnailImage = ({ file }: { file: any }) => {
  return (
    <div
      className='gallery-item-wrapper'
      onClick={(e) => {
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
      }}
      role='presentation'
    >
      <PhotoImage file={file} />
    </div>
  );
};

// DnD(드래그 앤 드롭) 기능이 추가된 캘린더 생성
const DnDCalendar = withDragAndDrop<Event, object>(Calendar);

// 캘린더 메인 컴포넌트
const BigCalendar: React.FC<BigCalendarProps> = ({
  events,
  refetch,
  queryClient,
  currentDate,
  setCurrentDate,
  onViewChange,
  auth,
  isSearchSmGubunAll,
  headInfoList,
  scheduleInfoList,
  userPartList,
  carInfoList,
  companyUser,
  hideOptionalToolBar = false,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const toDay = todayYYYYMMDD();
  const userInfo = useRecoilValue<IUser>(userState);
  // const queryClient = useQueryClient();
  // const [currentDate, setCurrentDate] = useState(new Date());

  // localStorage에서 저장된 뷰로 초기화 (lazy initialization 사용)
  const [currentView, setCurrentView] = useState<CalendarView>(() => getInitialView(hideOptionalToolBar));
  const prevViewRef = useRef<CalendarView | null>(null);

  const calendarWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onViewChange?.(currentView);
  }, [currentView, onViewChange]);

  useEffect(() => {
    if (onViewChange) {
      prevViewRef.current = currentView;
      return;
    }
    const prevView = prevViewRef.current;
    const isMultiWeekView = (view: CalendarView | null) => view === 'twoWeeks' || view === 'threeWeeks';
    if (prevView && prevView !== currentView) {
      const movedWithinMultiWeek = isMultiWeekView(prevView) && isMultiWeekView(currentView);
      const movedOutOfMultiWeek = isMultiWeekView(prevView) && !isMultiWeekView(currentView);
      if (movedWithinMultiWeek || movedOutOfMultiWeek) {
        refetch();
      }
    }
    prevViewRef.current = currentView;
  }, [currentView, onViewChange, refetch]);

  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false); // 필수값 여부
  const [openModal, setOpenModal] = useState<any>({ type: '', status: false });
  const [isNewSchedule, setIsNewSchedule] = useState<boolean>(true); // 신규 여부
  const [address, setAddress] = useState<IAddress>({ address1: '', postNum: '', latitude: '', longitude: '' }); // 주소 정보
  const [selectEvent, setSelectEvent] = useState(INIT_SCHEDULE); // 선택된 일정 데이터

  const [dateTime, setDateTime] = useState<any>({ start: toDay, end: toDay }); // 날짜
  const [rangeState, setRangeState] = useState<any>([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  const [selectedTimeStart, setSelectedTimeStart] = useState(INIT_START_HMS);
  const [selectedTimeEnd, setSelectedTimeEnd] = useState(INIT_HMS);
  const [availableTime, setAvailableTime] = useState<[{ start: Time; end: Time }]>([{ start: INIT_START_HMS, end: INIT_END_HMS }]);
  const [availableTimeRange, setAvailableTimeRange] = useState<{ start: Time; end: Time }>({ start: INIT_START_HMS, end: INIT_END_HMS });

  const [users, setUsers] = useState<{ label: string; value: string }[]>([]); // 선택된 참가자
  const [wGubun, setWGubun] = useState({ type: 'wGubun', v: '0', cdName: t('매주') }); // 반복일정 - 주간 - 기본값 매주
  const [hsCd1, setHsCd1] = useState({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') }); // 현장명 - 본사
  const [hsCd2, setHsCd2] = useState({ type: 'hsCd2', hsCd2: '', cdName: t('현장 선택') }); // 현장명 - 현장
  const [csCd, setCsCd] = useState({ type: 'csCd', csCd: '', cdName: t('공유항목 선택') }); // 공유 항목
  const [ccCd, setCcCd] = useState({ type: 'ccCd', ccCd: '', cdName: t('차량 선택') }); // 차량 정보
  const [dCd, setDCd] = useState({ type: 'dCd', dCd: '', cdName: t('운전자 선택') }); // 운전자
  const [isPaid, setIsPaid] = useState<string>('Y'); // 유/무상
  const [isEditable, setIsEditable] = useState(false); // 수정가능 권한소지 여부. 본인이 작성자이거나, 참가자 이거나, 마스터유저거나, 사내 관리자 권한일 때 수정가능
  const [gubunCheckState, setGubunCheckState] = useState(INIT_GUBUN_CHECK);
  const [isSlot, setIsSlot] = useState(true); // 등록된 항목 클릭 후 아무 키 입력시 빈 항목 이벤트가 같이 적용되는 버그 방지
  const [showOnlyMyEvents, setShowOnlyMyEvents] = useState(false); // 내 일정만 보기 여부
  const [initScheduleInfoData, setInitScheduleInfoData] = useState<{
    travelYn: boolean;
    hsCd1: string;
    hsCd2: string;
    isPaid: string;
    siteManagerName: string;
    siteManagerPhone: string;
    siteManagerPartname: string;
  }>({ travelYn: false, hsCd1: '', hsCd2: '', isPaid: 'Y', siteManagerName: '', siteManagerPhone: '', siteManagerPartname: '' }); // 초기 선택된 해당 일정 정보
  const [isSaving, setIsSaving] = useState<boolean>(false); // 저장 중 여부
  const [siteManagerName, setSiteManagerName] = useState<string>('');
  const [siteManagerPhone, setSiteManagerPhone] = useState<string>('');
  const [siteManagerPartname, setSiteManagerPartname] = useState<string>('');

  const smNameRef = useRef<HTMLInputElement>(null);
  const siteManagerNameRef = useRef<HTMLInputElement>(null);
  const siteManagerPhoneRef = useRef<HTMLInputElement>(null);
  // const contentsRef = useRef<HTMLTextAreaElement>(null);
  const hasScrolledToTodayRef = useRef<boolean>(false);

  // allDay 일정을 상단에 정렬하는 로직 - 최적화
  const sortedEvents = useMemo(() => {
    // 1. check가 true인 gubun 값들만 Set으로 저장 (한 번만 계산)
    const allowedGubuns = new Set(gubunCheckState.filter((item) => item.check).map((item) => item.gubun));

    // 2. events에서 gubun이 allowedGubuns에 포함된 것만 필터링 (공휴일은 휴가 구분(4)이지만 구분 필터와 무관하게 항상 표시)
    let filteredEvents = events.filter((event) => event.isHolidayEvent || allowedGubuns.has(event.smGubun));

    // 3. 내 일정만 보기 필터링 - 내가 참가자인 일정만 표시 (공휴일은 참가자가 없지만 항상 표시)
    if (showOnlyMyEvents) {
      filteredEvents = filteredEvents.filter((event) => {
        if (event.isHolidayEvent) return true;
        const currentUserId = userInfo.userId;
        // attenders 배열에 현재 사용자가 포함되어 있는지 확인
        const isInAttenders = event.attenders?.includes(currentUserId);

        // 참가자로만 필터링
        return isInAttenders;
      });
    }

    // 4. 휴가 일정의 start Date를 등록하여 캘린더 내부 정렬에서 마지막에 배치되도록 함
    filteredEvents.forEach((event) => {
      if (isPersonalDayOff(event) && event.start instanceof Date) dayOffStartDates.add(event.start);
    });

    // 5. Date 객체 변환을 최소화하기 위해 미리 계산
    const eventsWithTime = filteredEvents.map((event) => ({
      ...event,
      // start가 이미 Date 객체인 경우 getTime()만 호출, 아니면 변환
      startTime: event.start instanceof Date ? event.start.getTime() : new Date(event.start).getTime(),
    }));

    return eventsWithTime
      .sort((a, b) => {
        // 0. 휴가 일정은 항상 마지막
        if (isPersonalDayOff(a) !== isPersonalDayOff(b)) return isPersonalDayOff(a) ? 1 : -1;

        // 1. allDay 일정 우선 정렬
        if (a.allDay && !b.allDay) return -1;
        if (!a.allDay && b.allDay) return 1;

        // 2. 같은 allDay인 경우 id 기준으로 정렬
        if (a.allDay === b.allDay) return a.id - b.id;

        // 3. 미리 계산된 시간 기준으로 정렬 (Date 객체 생성 없음)
        return a.startTime - b.startTime;
      })
      .map(({ startTime, ...event }) => event); // startTime 제거하고 원본 이벤트만 반환
  }, [events, gubunCheckState, showOnlyMyEvents]);

  const [culture, setCulture] = useState('ko-KR');
  const [myEvents, setMyEvents] = useState<Event[]>(sortedEvents);
  const [popupData, setPopupData] = useState<PopupType | null>(null);
  const [popupSlide, setPopupSlide] = useState<'left' | 'right' | null>(null);
  const [isPopupInteractive, setIsPopupInteractive] = useState(false); // 팝업 터치 활성화 여부

  const handleShowMore = useCallback((moreEvents: Event[], date: Date) => {
    setIsSlot(false);
    // 데스크탑에서는 즉시 클릭 가능하도록 설정
    if (!mobile) {
      setIsPopupInteractive(true);
    }
    const target = document.querySelector('.rbc-show-more:hover') as HTMLElement;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const POPUP_WIDTH = 500;
    const POPUP_HEIGHT = 300;
    const GAP = 4; // 일정한 간격으로 수정

    const cellCenterX = rect.left + rect.width / 2; // 날짜 셀의 중앙 좌표 계산
    let x = cellCenterX - POPUP_WIDTH / 2; // 팝업의 x좌표 계산

    // 화면 좌우 경계 체 및 조정
    if (x < 10) {
      x = 10;
    } else if (x + POPUP_WIDTH > windowWidth - 10) {
      x = windowWidth - POPUP_WIDTH - 10;
    }

    let y = 0;
    let position: 'top' | 'bottom' = 'bottom';

    // 하단 공간이 부족한 경우 상단에 표시
    if (rect.bottom + POPUP_HEIGHT + GAP > windowHeight) {
      y = rect.top + window.scrollY - POPUP_HEIGHT - GAP;
      position = 'top';
    } else {
      y = rect.bottom + window.scrollY + GAP;
      position = 'bottom';
    }

    const arrowOffset = cellCenterX - x;

    setPopupData({ isOpen: true, date, events: moreEvents, position: { x, y, position, arrowOffset } });
  }, []);

  // 특정 날짜의 이벤트 수집 (월/주 뷰 기준: 날짜에 포함되는 모든 일정)
  const getEventsOfDate = useCallback(
    (date: Date) => {
      const d = dayjs(date).startOf('day');
      return myEvents.filter((ev) => {
        const s = dayjs(ev.start).startOf('day');
        const e = ev.end ? dayjs(ev.end).startOf('day') : s;
        // 날짜 범위 내 포함 (양끝 포함)
        return d.isSame(s, 'day') || d.isSame(e, 'day') || (d.isAfter(s, 'day') && d.isBefore(e, 'day'));
      });
    },
    [myEvents]
  );

  // 팝업에서 하루 단위 네비게이션
  const navigatePopupDay = useCallback(
    (delta: -1 | 1) => {
      if (!popupData?.date || !isPopupInteractive) return;
      setPopupSlide(delta < 0 ? 'left' : 'right');
      const nextDate = dayjs(popupData.date).add(delta, 'day').toDate();
      const nextEvents = getEventsOfDate(nextDate);
      // 간단한 슬라이드 애니메이션 시간과 동기화
      setTimeout(() => {
        setPopupData((prev) => (prev ? { ...prev, date: nextDate, events: nextEvents } : prev));
        setPopupSlide(null);
      }, 180);
    },
    [popupData?.date, getEventsOfDate, isPopupInteractive]
  );

  // 오버레이 클릭 시 팝업 닫기
  const closePopup = useCallback(() => {
    // 터치가 비활성화된 상태에서는 닫지 않음 (아이폰 터치 이벤트 버블링 방지)
    if (!isPopupInteractive) {
      return;
    }

    setIsSlot(true);
    setIsPopupInteractive(false);

    // 애니메이션을 위한 클래스 제거
    setPopupData((prev: PopupType | null) => ({ ...prev, isClosing: true } as PopupType));

    // 애니메이션이 끝난 후 팝업 완전히 제거
    setTimeout(() => {
      setPopupData(null);
    }, 300);
  }, [isPopupInteractive]);

  const { onBackPrev: onBackPrevPopup } = useBackHistory({
    isOpen: popupData?.isOpen ?? false,
    onPrev: closePopup,
    pageName: 'schedule_popover',
  });

  // 시간 포멧팅 정의 - { h, m, s } -> 'hhmm'
  const setTimeFormat = (timeObj: Time) => {
    const arr = [];
    for (const val of Object.values(timeObj)) {
      arr.push(val.toString().padStart(2, '0'));
    }

    return `${arr.join('').slice(0, 4)}`;
  };

  // 시간/분/초 => 밀리 세컨드 변환
  const calcTotalSencond = (time: Time) => {
    return time.h * 3600 + time.m * 60 + time.s;
  };

  // 시간 범위 체크 ( 종료시간이 시작시간보다 클 경우에 대한 체크 )
  const isTimeChecked = ({ start, end }: { start: Time; end: Time }): boolean => {
    let checked = false;
    const startTotalSeconds = calcTotalSencond(start);
    const endTotalSeconds = calcTotalSencond(end);

    if (startTotalSeconds >= endTotalSeconds) checked = true;

    return checked;
  };

  useEffect(() => {
    if (sortedEvents) setMyEvents(sortedEvents);
  }, [sortedEvents]);
  /**
   * react-big-calendar의 Month(2주/3주 포함)는 마운트 시 1회만 rowLimit(셀당 표시 개수)을 측정하고,
   * 이후에는 window 'resize' 시에만 재측정한다. 하드 새로고침 직후 툴바/타이틀 레이아웃이 뒤늦게
   * 정착되며 월 뷰 높이가 커지는데, 이때 rbc는 이미 (짧은 행 높이로) 측정을 마쳐 '+ 추가 일정 보기'가
   * 과도하게 노출된다. 실제로 높이가 변하는 '.rbc-month-view'를 관찰해 그 순간 resize를 발생시켜
   * rbc가 올바른 행 높이로 다시 측정하도록 한다.
   */
  useEffect(() => {
    const fire = () => window.dispatchEvent(new Event('resize'));
    const root = calendarWrapperRef.current;
    if (!root) return () => {};

    let observer: ResizeObserver | null = null;
    let rafId = 0;
    let tries = 0;

    // rbc가 rowLimit 측정 기준으로 삼는 월 뷰의 높이 변화를 관찰
    const attach = () => {
      const monthView = root.querySelector('.rbc-month-view') as HTMLElement | null;
      if (monthView && typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(() => requestAnimationFrame(fire));
        observer.observe(monthView); // 최초 1회 + 크기 변경 시마다 재측정 유도
        return;
      }
      // 마운트 직후 월 뷰가 아직 없을 수 있어 몇 프레임 재시도
      if (tries < 15) {
        tries += 1;
        rafId = requestAnimationFrame(attach);
      }
    };
    attach();

    // 안전망: 폰트 로드 및 지연 리플로우(~1초) 이후 재측정
    const fontsReady = (document as any).fonts?.ready ?? Promise.resolve();
    fontsReady.then(fire);
    const timers = [200, 600, 1200].map((ms) => window.setTimeout(fire, ms));

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
      timers.forEach((id) => clearTimeout(id));
    };
  }, [currentView, isLoading]);

  const addSchedule = async (schedule: any) => {
    try {
      // schedule에서 smName 추출 (FormData 또는 일반 객체 모두 처리)
      const scheduleObj = schedule instanceof FormData ? Object.fromEntries(schedule.entries()) : schedule;
      const smName = scheduleObj.smName || '';

      // 성공 시 공통 처리 함수
      const handleAddSuccess = async () => {
        setInitScheduleInfoData({ travelYn: false, hsCd1: hsCd1.hsCd1, hsCd2: hsCd2.hsCd2, isPaid: 'Y', siteManagerName, siteManagerPhone, siteManagerPartname });
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정관리(캘린더)',
          action: '일정 추가',
          etc: `일정명 : ${smName}`,
        });
      };

      if (window.FormSubmit) {
        try {
          const transformedForm = transformFormData(schedule);
          await submitFormToFlutterWithCallback({ url: '/schedule', method: 'POST', form: transformedForm });
          await handleAddSuccess();
        } catch (err) {
          toast.error(t('일정 추가에 실패했습니다'));
          throw err;
        }
      } else {
        const res = await apiPost({ path: '/schedule', contentType: 'multipart/form-data', req: schedule });
        const { statusCode } = res.data;
        if (statusCode === 200) {
          await handleAddSuccess();
        } else {
          toast.error(t('일정 추가에 실패했습니다'));
        }
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const dragUpdateSchedule = async (schedule: any) => {
    try {
      const { smIdx, smGubun, orgTitle, travelYn, desc, start, end, orgRepeatSdate, attenders, address: adrs, latitude, longitude, allDay } = schedule;
      // 종일 일정인 경우 end 날짜 처리 (end가 해당일 23:59:59이므로 그대로 사용)
      const fDate = dayjs(end).format('YYYYMMDD');
      const fTime = allDay ? '2359' : dayjs(end).format('HHmm');
      const setData = {
        cCd: userInfo.hCd,
        smIdx,
        smGubun,
        smName: orgTitle,
        travelYn: selectEvent.smGubun === '3' ? (travelYn ? 'Y' : 'N') : 'N',
        contents: desc,
        sDate: dayjs(start).format('YYYYMMDD'), // 업데이트 하는 날짜
        fDate,
        sTime: allDay ? '0000' : dayjs(start).format('HHmm'),
        fTime,
        users: attenders,
        address: adrs,
        latitude,
        longitude,
        alldayYn: allDay === true ? 'Y' : 'N',
        csCd: schedule.csCd,
        siteManagerName: schedule?.siteManagerName || '',
        siteManagerPhone: schedule?.siteManagerPhone || '',
        siteManagerPartname: schedule?.siteManagerPartname || '',
        /*
         * 수정자 : 홍선영
         * 수정일자 : 2025.01.20
         * 수정내용 : 반복일정은 드래그로 수정할수 없으므로 아래 주석처리
         */
        // dGubun: selectEvent.dGubun.sort((a: string, b: string) => +a - +b).join('|'),
      };
      const req = getReq({ ...setData });
      const res = await apiPatch({ path: '/schedule', contentType: 'multipart/form-data', req });
      const { statusCode } = res.data;
      if (statusCode === 200) {
        toast.success(t('일정이 수정되었습니다'));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정관리(캘린더)',
          action: '일정 수정(드래그)',
          etc: `일정 명 : ${schedule.smName}`,
        });
      } else toast.error(t('일정 수정에 실패했습니다'));
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const saveUpdateSchedule = async (schedule: any, saveType: string) => {
    try {
      const isRepeat = saveType === 'repeat';
      const path = isRepeat ? '/schedule/repeat' : '/schedule';
      const files = { files: selectEvent.fileList.filter((el: IFile) => el.fUrl === '').map((el: IFile) => el.file) };
      const dFileIdx = { deleteFileIndex: schedule.deleteFileIndex.map((idx: number) => idx.toString()) };
      const setData = {
        smIdx: schedule.smIdx.toString(),
        cCd: userInfo.hCd,
        smGubun: schedule.smGubun,
        smName: schedule.smName,
        sDate: dateTime.start.replaceAll('-', ''),
        sTime: setTimeFormat(selectedTimeStart),
        fDate: dateTime.end.replaceAll('-', ''),
        fTime: setTimeFormat(selectedTimeEnd),
        contents: schedule.contents,
        users: selectEvent.smGubun === '2' || selectEvent.smGubun === '3' ? users.map((el: any) => el.value) : [],
        alldayYn: schedule.alldayYn,
        completeYn: schedule.completeYn,
        travelYn: selectEvent.smGubun === '3' ? schedule.travelYn : 'N',
        importantYn: schedule.importantYn,
        hsCd: `${hsCd1.hsCd1}${hsCd2.hsCd2}`,
        csCd: schedule.smGubun === '3' ? csCd.csCd : '',
        ccCd: ccCd.ccCd,
        dId: dCd.dCd,
        isPaid,
        wGubun: selectEvent.dGubun.join('') !== '' ? selectEvent.wGubun : '',
        dGubun: schedule.dGubun.sort((a: string, b: string) => +a - +b).join('|'),
        address: address.address1,
        latitude: address.latitude,
        longitude: address.longitude,
        siteManagerName,
        siteManagerPhone: siteManagerPhone.replace(/\D/g, ''),
        siteManagerPartname,
        ...(files.files.length !== 0 ? files : {}), // 파일 리스트
        ...(dFileIdx.deleteFileIndex.length !== 0 ? dFileIdx : {}), // 기존 파일 삭제한 아이디
        ...(isRepeat ? { updateDate: selectEvent.orgRepeatSdate } : {}),
      };
      const req = getReq({ ...setData });

      // 성공 시 공통 처리 함수
      const handleUpdateSuccess = async () => {
        setInitScheduleInfoData({
          travelYn: selectEvent.smGubun === '3' ? schedule.travelYn === 'Y' : false,
          hsCd1: hsCd1.hsCd1,
          hsCd2: hsCd2.hsCd2,
          isPaid,
          siteManagerName,
          siteManagerPhone,
          siteManagerPartname,
        });
        toast.success(t('일정이 수정되었습니다'));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정관리(캘린더)',
          action: '일정 수정',
          etc: `일정 명 : ${schedule.smName}`,
        });
      };

      if (window.FormSubmit) {
        try {
          const transformedForm = transformFormData(req);
          await submitFormToFlutterWithCallback({ url: '/schedule', method: 'PATCH', form: transformedForm });
          await handleUpdateSuccess();
        } catch (err) {
          toast.error(t('일정 수정에 실패했습니다'));
          throw err;
        }
      } else {
        const res = await apiPatch({ path, contentType: 'multipart/form-data', req });
        const { statusCode } = res.data;
        if (statusCode === 200) {
          await handleUpdateSuccess();
        } else {
          toast.error(t('일정 수정에 실패했습니다'));
        }
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const deleteSchedule = async (deleteType: string) => {
    try {
      const isRepeat = deleteType === 'repeat';
      const path = isRepeat ? '/schedule/repeat' : '/schedule';
      const req = {
        smIdx: Number(selectEvent.smIdx),
        cCd: userInfo.hCd,
        ...(isRepeat ? { updateDate: selectEvent.orgRepeatSdate } : {}),
        ...(!isRepeat && selectEvent.repeatsmIdx ? { repeatsmIdx: selectEvent.repeatsmIdx } : {}),
      };

      await apiDelete({ path, contentType: 'application/json', req });
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '일정관리(캘린더)',
        action: `일정 삭제`,
        etc: `일정 명 : ${selectEvent.smName}`,
      });
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 일정 추가 mutation
  const addEventMutation = useMutation({
    mutationFn: (newEvent: Omit<Event, 'id'>) => {
      return addSchedule(newEvent);
    },
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setIsSlot(true);
      setOpenModal((prev: IModal) => ({ ...prev, status: false })); // 성공 시 모달 닫기
      toast.success(t('일정이 추가되었습니다'));
    },
    onError: (error: any) => {
      console.error('일정 추가 실패:', error);
      toast.error(t('일정 추가에 실패했습니다'));
    },
  });

  // 일정 업데이트 mutation
  const updateEventMutation = useMutation({
    mutationFn: (updatedEvent: any) => {
      const [uEvent, saveType] = updatedEvent;
      setIsSaving(true);
      if (uEvent?.mode === 'drag') {
        return dragUpdateSchedule(uEvent); // API 호출
      }
      return saveUpdateSchedule(uEvent, saveType);
    },
    onSuccess: () => {
      // refetch(); // 원복용: invalidateQueries(['schedules'])가 활성 쿼리를 재조회하므로 중복 조회 방지를 위해 주석 처리
      // 성공 시 캐시된 일정 데이터 갱신
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setIsSlot(true);
      setOpenModal((prev: IModal) => ({ ...prev, status: false })); // 성공 시 모달 닫기
      setIsSaving(false);
      // toast.success('일정이 수정되었습니다');
    },
    onError: (error: any) => {
      console.error('일정 업데이트 실패:', error);
      toast.error(t('일정 수정에 실패했습니다'));
      setIsSaving(false);
    },
  });

  // 일정 삭제 mutation
  const deleteEventMutation = useMutation({
    mutationFn: (deleteType: string) => {
      return deleteSchedule(deleteType); // API 호출
    },
    onSuccess: () => {
      refetch();
      // 성공 시 캐시된 일정 데이터 갱신
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setIsSlot(true);
      setOpenModal((prev: IModal) => ({ ...prev, status: false })); // 성공 시 모달 닫기

      // setPopupData((prev: any) => ({ ...prev, events: events.filter((el: any) => el.orgSdate === dayjs(prev.date).format('YYYYMMDD')) }));

      toast.success(t('일정이 삭제되었습니다'));
    },
    onError: (error: any) => {
      console.error('일정 삭제 실패:', error);
      toast.error(t('일정 삭제에 실패했습니다'));
    },
  });

  const msg = (type: string) => toast.warn(`${type} ${t('일정은 드래그할 수 없습니다')}`);

  // 이벤트 드래그 앤 드롭 처리
  const moveEvent = useCallback(
    (args: EventInteractionArgs<Event>) => {
      const updateEvent = (arg: EventInteractionArgs<Event>) => {
        const { event, start, end, isAllDay: droppedOnAllDaySlot = false } = arg;
        let { allDay } = event;

        // 일반 일정을 종일 일정 영역에 드롭한 경우
        if (!allDay && droppedOnAllDaySlot) {
          allDay = true;
        }

        // 종일 일정을 일반 일정 영역에 드롭한 경우
        if (allDay && arg.isAllDay === undefined && currentView !== 'month' && currentView !== 'twoWeeks' && currentView !== 'threeWeeks') {
          allDay = false;
        }

        // 종일 일정인 경우 start와 end를 날짜 기준으로 조정
        let adjustedStart = new Date(start);
        let adjustedEnd = new Date(end);

        if (allDay) {
          // 종일 일정의 경우 args의 start/end에서 날짜만 추출 (시간대 변환 문제 무시)
          // 로컬 날짜 기준으로 YYYY-MM-DD 형식으로 추출
          const startDateStr = dayjs(start).format('YYYY-MM-DD');
          const endDateStr = dayjs(end).format('YYYY-MM-DD');

          // 추출한 날짜 문자열로 새로운 Date 객체 생성 (로컬 시간대 기준)
          const startDate = dayjs(startDateStr).startOf('day').toDate();

          // start와 end가 같은 날짜인 경우 1일 일정으로 처리
          if (startDateStr === endDateStr) {
            // 같은 날짜로 드래그한 경우: start 날짜의 23:59:59
            adjustedEnd = dayjs(startDateStr).endOf('day').toDate();
          } else {
            // 다른 날짜로 드래그한 경우: end 날짜의 23:59:59
            adjustedEnd = dayjs(endDateStr).endOf('day').toDate();
          }

          adjustedStart = startDate;
        }

        // 이벤트 업데이트 로직
        const updatedEvent = {
          ...event,
          start: adjustedStart,
          end: adjustedEnd,
          allDay,
          mode: 'drag',
        };

        // 로컬 상태 업데이트
        setMyEvents((prev) => {
          const filtered = prev.filter((ev) => ev.id !== event.id);
          return [...filtered, updatedEvent];
        });

        // 서버에 업데이트 요청
        updateEventMutation.mutate([updatedEvent]);
      };

      const { complete, isDayOffEvent, isHolidayEvent, isRepeatSchedule } = args.event;

      // 완료된 일정, 근태·공휴일 일정, 반복일정 드래그 불가
      // 본인작성한 일정이거나 마스터유저, 사내관리자유저 경우 드래그 가능
      if (!auth.updateAuth) return toast.warn(t('일정 수정 권한이 없습니다'));
      if (complete) return msg(t('완료된'));
      if (isDayOffEvent) return msg(t(isHolidayEvent ? '공휴일' : '근태'));
      if (isRepeatSchedule) return msg(t('반복'));
      // 모달 열기
      return setOpenModal({
        type: 'confirm',
        status: true,
        text: t('정말 수정하시겠습니까?'),
        api: () => updateEvent(args),
      });
      // return updateEvent(args);
    },
    [setMyEvents, currentView]
  );

  // 일정 추가 핸들러
  const handleAddEvent = async (newEventData: any) => {
    setIsSaving(true);
    setIsNewSchedule(true); // 신규등록
    setSiteManagerName('');
    setSiteManagerPhone('');
    setSiteManagerPartname('');
    try {
      await addEventMutation.mutateAsync(newEventData);
      setIsSaving(false);
    } catch (error) {
      console.error('일정 추가 중 오류 발생:', error);
      setIsSaving(false);
    }
  };

  // 초기화
  const initSet = useCallback(() => {
    setIsSaveClicked(false);
    setHsCd1({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') });
    setHsCd2({ type: 'hsCd2', hsCd2: '', cdName: t('현장 선택') });
    setWGubun({ type: 'wGubun', v: '0', cdName: t('매주') });
    setCsCd({ type: 'csCd', csCd: '', cdName: t('공유항목 선택') });
    setCcCd({ type: 'ccCd', ccCd: '', cdName: t('차량 선택') });
    setDCd({ type: 'dCd', dCd: '', cdName: t('운전자 선택') });

    setDateTime({ start: toDay, end: toDay });
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    setSelectedTimeStart(INIT_START_HMS);
    setSelectedTimeEnd(INIT_HMS);
    setAvailableTime([{ start: INIT_START_HMS, end: INIT_END_HMS }]);
    setAddress({ address1: '', postNum: '', latitude: '', longitude: '' });
    setUsers([]);
    setSelectEvent(INIT_SCHEDULE);
    setIsPaid('Y');
    setInitScheduleInfoData({ travelYn: false, hsCd1: '', hsCd2: '', isPaid: 'Y', siteManagerName: '', siteManagerPhone: '', siteManagerPartname: '' });
  }, [t, toDay]);

  // 일정 추가 클릭 핸들러
  const handleNewEvent = () => {
    if (!auth.createAuth) toast.warn(t('일정 추가 권한이 없습니다'));
    else {
      initSet();
      setIsNewSchedule(true);
      handleAddEventClick();
      setSiteManagerName('');
      setSiteManagerPhone('');
      setSiteManagerPartname('');
    }
  };

  // 일정 추가 팝업 열기/닫기 핸들러
  const handleAddEventClick = useCallback(() => {
    setOpenModal({ type: 'schedule', status: true });
  }, []);

  const handleCloseAddEventPopup = useCallback(() => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
  }, []);

  const onClickAddSchedule = (saveType: any) => {
    setIsSaveClicked(true);

    const setData = {
      cCd: userInfo.hCd,
      smName: selectEvent.smName,
      sDate: dateTime.start.replaceAll('-', ''),
      fDate: dateTime.end.replaceAll('-', ''),
      sTime: setTimeFormat(selectedTimeStart),
      fTime: setTimeFormat(selectedTimeEnd),
      alldayYn: selectEvent.alldayYn,
      contents: selectEvent.contents || '',
      completeYn: selectEvent.completeYn,
      travelYn: selectEvent.smGubun === '3' ? selectEvent.travelYn : 'N',
      importantYn: selectEvent.importantYn,
      smGubun: selectEvent.smGubun,
      users: selectEvent.smGubun === '2' || selectEvent.smGubun === '3' ? users.map((el: any) => el.value) : [],
      hsCd: `${hsCd1.hsCd1}${hsCd2.hsCd2}`,
      csCd: selectEvent.smGubun === '3' ? csCd.csCd : '',
      ccCd: ccCd.ccCd,
      dId: dCd.dCd,
      isPaid,
      wGubun: selectEvent.dGubun.join('') !== '' ? selectEvent.wGubun : '',
      dGubun: selectEvent.dGubun.sort((a: string, b: string) => +a - +b).join('|') || [],
      address: address.address1,
      latitude: address.latitude,
      longitude: address.longitude,
      files: selectEvent.fileList.map((el: IFile) => el.file), // 파일 리스트
      siteManagerName,
      siteManagerPhone: siteManagerPhone.replace(/\D/g, ''),
      siteManagerPartname,
      // ...(selectEvent.isRepeatSchedule ? { orgSdate: selectEvent.orgSdate, orgStime: selectEvent.orgStime, orgFdate: selectEvent.orgFdate, orgFtime: selectEvent.orgFtime } : {}),
    };
    const newRowRequired: string[] = ['smName'];
    // const newRowRequired: string[] = setData.travelYn === 'Y' ? ['smName', 'siteManagerName', 'siteManagerPhone'] : ['smName'];
    const check: boolean = newRowRequired.some((el: string) => (setData as any)[el] === '');

    // 반복일정인 경우 일자와 반복 요일이 일치하는 지 체크
    if (selectEvent.loopYn === 'Y') {
      const startDate = new Date(dateTime.start);
      const endDate = new Date(dateTime.end);

      // 시작일과 종료일의 차이 계산 (일 단위)
      const diffTime = endDate.getTime() - startDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // 7일 이하인 경우에만 요일 체크 수행
      if (diffDays <= 7) {
        // 시작일부터 종료일까지 각 날짜의 요일이 선택된 요일 목록에 포함되는지 확인
        const curDate = new Date(startDate);
        let hasValidDay = false; // 선택된 요일이 하나라도 있는지 확인하는 플래그

        while (curDate <= endDate) {
          const currentDay = curDate.getDay().toString(); // 0(일요일) ~ 6(토요일)

          // 현재 날짜의 요일이 선택된 요일 목록에 있는 경우
          if (selectEvent.dGubun.includes(currentDay)) {
            hasValidDay = true;
            break; // 하나라도 발견되면 더 이상 체크할 필요 없음
          }

          // 다음 날짜로 이동
          curDate.setDate(curDate.getDate() + 1);
        }

        // 선택된 요일이 하나도 없는 경우
        if (!hasValidDay) {
          toast.warning(t('선택한 기간에 해당하는 반복 요일이 없습니다.'));
          return false;
        }
      }
    }

    if (check) {
      toast.warning(t('필수입력값을 모두 입력하세요'));
      return false;
    }

    // 출장일정일 때만 본사선택/현장선택 필수입력 체크
    if (selectEvent.travelYn === 'Y') {
      if (hsCd1.hsCd1 === '' || hsCd2.hsCd2 === '') {
        toast.warning(t('출장 일정 등록 시 현장 선택은 필수입니다'));
        return false;
      }
    }

    // 기간이 같을 때 또는 반복일 경우에는 종료시간이 시작시간보다 클 경우
    if (
      (dateTime.start === dateTime.end && isTimeChecked({ start: selectedTimeStart, end: selectedTimeEnd })) ||
      (selectEvent.loopYn === 'Y' && isTimeChecked({ start: selectedTimeStart, end: selectedTimeEnd }))
    ) {
      toast.warning(t('시작시간이 종료시간보다 같거나 클 수 없습니다'));
      return false;
    }

    // // 출장일정 참가자 목록에 인원이 없는 경우 예외처리
    // if (selectEvent.smGubun === '3' && selectEvent.travelYn === 'Y' && setData.users.length === 0) {
    //   toast.warning(t('출장 일정의 경우 참가자가 있어야 합니다'));
    //   return false;
    // }

    const req = getReq({ ...setData });

    isNewSchedule ? handleAddEvent(req) : updateEventMutation.mutate([selectEvent, saveType]);
    return false;
  };

  const deleteAPI = (deleteType: string) => {
    deleteEventMutation.mutate(deleteType);
  };

  // Toolbar에 handleNewEvent 연결
  const toolbarComponent = useCreateToolbarWithProps(
    auth,
    culture,
    handleNewEvent,
    gubunCheckState,
    setGubunCheckState,
    isSearchSmGubunAll,
    hideOptionalToolBar,
    refetch,
    showOnlyMyEvents,
    setShowOnlyMyEvents,
    (v: CalendarView) => onView(v),
    isLoading
  );

  // 일정 항목 클릭 이벤트
  const handleSelectEvent = useCallback(
    (event: Event) => {
      setIsNewSchedule(false);
      setUsers(event.userId.map((el: any) => ({ label: el.userName, value: el.userId })));
      setHsCd1((prev: any) => ({ ...prev, hsCd1: event.hsCd1, cdName: event.hName }));
      setHsCd2((prev: any) => ({ ...prev, hsCd2: event.hsCd2, cdName: event.sName }));
      setWGubun((prev: any) => ({ ...prev, v: event.wGubun }));
      event.csCd !== '' ? setCsCd((prev: any) => ({ ...prev, csCd: event.csCd })) : setCsCd({ type: 'csCd', csCd: '', cdName: t('공유항목 선택') });
      setCcCd((prev: any) => ({ ...prev, ccCd: event.ccCd }));
      setDCd((prev: any) => ({ ...prev, dCd: event.dCd }));
      setDateTime({ start: event.orgSdate, end: event.orgFdate });
      setRangeState([{ key: 'selection', startDate: formatDateType(`${event.orgSdate}${event.orgStime}00`), endDate: formatDateType(`${event.orgFdate}${event.orgFtime}00`) }]);
      setSelectedTimeStart({ h: Number(event.orgStime.slice(0, 2)), m: Number(event.orgStime.slice(2, 4)), s: 0 });
      setSelectedTimeEnd({ h: Number(event.orgFtime.slice(0, 2)), m: Number(event.orgFtime.slice(2, 4)), s: 0 });
      setIsPaid(event.isPaid);
      setAddress({
        address1: event.address,
        latitude: event.latitude,
        longitude: event.longitude,
        postNum: '',
      });
      setSelectEvent({
        smIdx: event.smIdx,
        smGubun: event.smGubun,
        completeYn: event.complete ? 'Y' : 'N',
        travelYn: event.travelYn ? 'Y' : 'N',
        smName: event.smName,
        importantYn: event.important ? 'Y' : 'N',
        loopYn: event.isRepeatSchedule ? 'Y' : 'N',
        fontColor: event.fontColor ? event.fontColor : '#000000',
        backColor: event.backColor ? event.backColor : '#f1f1f1',
        isRepeatSchedule: event.isRepeatSchedule,
        orgRepeatSdate: event.orgRepeatSdate ? event.orgRepeatSdate : '',
        setAttenders: event.setAttenders.map((el: any) => ({ label: el.userName, value: el.userId })),
        alldayYn: event.allDay ? 'Y' : 'N',
        contents: event.desc || '',
        repeatsmIdx: event.repeatsmIdx,
        wGubun: event.wGubun,
        dGubun: event.dGubun,
        csCd: event.csCd,
        ccCd: event.ccCd,
        dCd: event.dCd,
        hsCd1: event.hsCd1,
        hsCd2: event.hsCd2,
        fileList: event.files,
        deleteFileIndex: [],
        writer: event.writer,
        wDate: event.wDate,
        editor: event.editor,
        eDate: event.eDate,
        address1: event.address,
        latitude: event.latitude,
        longitude: event.longitude,
        users: event.users,
        siteManagerName: event.siteManagerName || '',
        siteManagerPhone: event.siteManagerPhone || '',
        siteManagerPartname: event.siteManagerPartname || '',
      });
      setSiteManagerName(event.siteManagerName || '');
      setSiteManagerPhone(event.siteManagerPhone || '');
      setSiteManagerPartname(event.siteManagerPartname || '');
      setIsEditable(auth.updateAuth);
      // 선택된 해당 일정의 초기 정보 담기
      setInitScheduleInfoData({
        travelYn: event.travelYn,
        hsCd1: event.hsCd1,
        hsCd2: event.hsCd2,
        isPaid: event.isPaid,
        siteManagerName: event.siteManagerName || '',
        siteManagerPhone: event.siteManagerPhone || '',
        siteManagerPartname: event.siteManagerPartname || '',
      });

      // 휴가 스케줄은 팝업창 x
      if (event.smGubun !== '4') {
        handleAddEventClick();
      }
    },
    [t, handleAddEventClick]
  );

  const memoizedEventComponent = useCallback((props: any) => <CustomEvent {...props} currentView={currentView} />, [currentView]);

  const memoizedCustomShowMore = useCallback((props: any) => <CustomShowMore {...props} handleClickShowMore={handleShowMore} />, [handleShowMore]);

  // 커스텀 Agenda Event 컴포넌트
  const memoizedAgendaEventComponent = useCallback(({ event: agendaEvent }: { event: Event }) => <CustomAgendaEvent event={agendaEvent} onSelect={handleSelectEvent} />, [handleSelectEvent]);

  // Agenda 뷰일 때는 항상 해당 월의 1일로 설정 (오늘 이전 날짜도 렌더링되도록)
  const agendaDate = React.useMemo(() => {
    if (currentView === Views.AGENDA) {
      return dayjs(currentDate).startOf('month').toDate();
    }
    return currentDate;
  }, [currentView, currentDate]);

  // Agenda Date 컴포넌트
  // gubunCheckState를 문자열 키로 직렬화하여 불필요한 리렌더 방지
  const gubunKey = useMemo(() => gubunCheckState.map((g: any) => `${g.gubun}:${g.check ? 1 : 0}`).join('|'), [gubunCheckState]);

  const MemoizedAgendaDate = useCallback(
    ({ label }: { label?: string }) => <AgendaDateWrapper label={label} userPartList={userPartList} myEvents={events} gubunKey={gubunKey} agendaDate={agendaDate} />,
    [userPartList, events, gubunKey, agendaDate]
  );

  const MemoizedMonthDateHeader = useCallback(
    ({ label, date, isOffRange }: { label: string; date: Date; isOffRange?: boolean }) => <CustomMonthDateHeader label={label} date={date} myEvents={events} isOffRange={isOffRange} />,
    [events]
  );

  // 1주/2주/3주 뷰용: 현재 달 기준 흐림(isOffRange) 처리 없이 공휴일 색상만 적용
  const MemoizedDateHeader = useCallback(({ label, date }: { label: string; date: Date }) => <CustomMonthDateHeader label={label} date={date} myEvents={events} />, [events]);

  const { components, defaultDate } = useMemo(() => {
    return {
      components: {
        event: memoizedEventComponent, // CustomEvent 설정
        // eventWrapper: MyEventWrapper,
        // eventContainerWrapper: MyEventContainerWrapper,
        // dateCellWrapper: CustomDateCellWrapper, // DateCell 커스터마이징
        // dayColumnWrapper: MyDayColumnWrapper,
        // timeSlotWrapper: MyTimeSlotWrapper,
        // timeGutterHeader: MyTimeGutterWrapper,
        // resourceHeader: MyResourceHeader,
        toolbar: toolbarComponent,
        showMore: memoizedCustomShowMore,
        agenda: {
          event: memoizedAgendaEventComponent,
          date: MemoizedAgendaDate,
          time: CustomAgendaTime,
        },
        day: {
          header: MemoizedDateHeader,
          event: memoizedEventComponent,
        },
        week: {
          header: MemoizedDateHeader,
          event: memoizedEventComponent,
        },

        // 커스텀 뷰에도 이벤트 컴포넌트 적용
        twoWeeks: { event: memoizedEventComponent, header: CustomMonthDateHeader, dateHeader: MemoizedDateHeader },
        threeWeeks: { event: memoizedEventComponent, header: CustomMonthDateHeader, dateHeader: MemoizedDateHeader },
        month: {
          header: CustomMonthDateHeader, // 요일 헤더는 일정 데이터 없이 요일 색상만 적용
          dateHeader: MemoizedMonthDateHeader,
          //   event: MyMonthEvent,
        },
        eventWrapper: EventWrapper,
      },
      defaultDate: new Date(),
    };
  }, [handleAddEventClick, culture, gubunCheckState, isSearchSmGubunAll, memoizedEventComponent, toolbarComponent, MemoizedAgendaDate, MemoizedMonthDateHeader, MemoizedDateHeader]);

  const { messages } = useMemo(
    () => ({
      messages: lang[i18n.language],
    }),
    [culture]
  );

  useEffect(() => {
    setCulture(i18n.language);
  }, [i18n.language]);

  // 팝업 상태 변경 감지 및 정리
  useEffect(() => {
    if (!popupData?.isOpen) {
      // 애니메이션을 위한 클래스 제거
      setPopupData((prev: PopupType | null) => (prev ? { ...prev, isClosing: true } : null));

      // 애니메이션 후에 상태를 완전히 제거
      const timer = setTimeout(() => {
        setPopupData(null);
      }, 300);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [popupData?.isOpen]);

  // myEvents가 변경되고 더보기 모달이 열려있을 때 popupData.events 업데이트
  // 모달이 열릴 때는 캘린더가 넘겨준 순서를 유지하도록 myEvents가 실제로 바뀐 경우에만 갱신
  const prevMyEventsRef = useRef(myEvents);
  useEffect(() => {
    if (prevMyEventsRef.current === myEvents) return;
    prevMyEventsRef.current = myEvents;
    if (popupData?.isOpen && popupData?.date) {
      const updatedEvents = getEventsOfDate(popupData.date);
      setPopupData((prev) => (prev ? { ...prev, events: updatedEvents } : prev));
    }
  }, [myEvents, popupData?.isOpen, popupData?.date, getEventsOfDate]);

  // 오늘 날짜로 스크롤하는 함수
  const scrollToToday = React.useCallback(() => {
    const today = dayjs();
    const todayPrefix = today.format('MM.DD');
    const dateCells = document.querySelectorAll('.rbc-agenda-date-cell');

    if (dateCells.length === 0) return;

    for (let i = 0; i < dateCells.length; i += 1) {
      const cell = dateCells[i];
      const cellText = cell.textContent?.trim();

      if (cellText && cellText.startsWith(todayPrefix)) {
        const row = cell.closest('tr') as HTMLTableRowElement;
        if (row) {
          const cellDate = dayjs(cellText, 'MM.DD ddd');
          if (cellDate.isSame(today, 'day')) {
            const agendaContainer = document.querySelector('.rbc-agenda-view') as HTMLElement;
            if (agendaContainer) {
              const rowRect = row.getBoundingClientRect();
              const containerRect = agendaContainer.getBoundingClientRect();
              const scrollTop = agendaContainer.scrollTop + (rowRect.top - containerRect.top);
              agendaContainer.scrollTop = Math.max(0, scrollTop);
            }
            return;
          }
        }
      }
    }
  }, []);

  // Agenda view에서 오늘 날짜 행으로 스크롤 (최적화 - 지연 실행)
  useEffect(() => {
    if (currentView !== Views.AGENDA) {
      hasScrolledToTodayRef.current = false;
      return undefined;
    }

    const today = dayjs();
    const currentMonth = dayjs(currentDate);

    // 오늘 날짜와 같은 월이 아니면 스크롤하지 않음
    if (currentMonth.year() !== today.year() || currentMonth.month() !== today.month()) {
      hasScrolledToTodayRef.current = false;
      return undefined;
    }

    // 이미 스크롤했으면 다시 실행하지 않음
    if (hasScrolledToTodayRef.current) {
      return undefined;
    }

    // requestIdleCallback을 사용하여 브라우저가 유휴 상태일 때 실행
    let timer: NodeJS.Timeout | null = null;
    let idleId: number | NodeJS.Timeout | null = null;

    if (window.requestIdleCallback) {
      idleId = window.requestIdleCallback(
        () => {
          // DOM이 완전히 렌더링될 때까지 추가 대기
          timer = setTimeout(() => {
            const todayPrefix = today.format('MM.DD');

            // 오늘 날짜를 포함하는 날짜 셀 찾기 (한 번만 쿼리)
            const dateCells = document.querySelectorAll('.rbc-agenda-date-cell');

            if (dateCells.length === 0) {
              // DOM이 아직 준비되지 않았으면 다시 시도하지 않음
              return;
            }

            // for 루프 사용 (forEach보다 빠름)
            for (let i = 0; i < dateCells.length; i += 1) {
              const cell = dateCells[i];
              const cellText = cell.textContent?.trim();

              // 날짜 prefix가 일치하는 경우만 확인
              if (cellText && cellText.startsWith(todayPrefix)) {
                const row = cell.closest('tr') as HTMLTableRowElement;
                if (row) {
                  // 정확히 오늘 날짜인지 확인 (dayjs 호출 최소화)
                  const cellDate = dayjs(cellText, 'MM.DD ddd');
                  if (cellDate.isSame(today, 'day')) {
                    hasScrolledToTodayRef.current = true;
                    // 상단이 잘리지 않도록 약간의 여유를 두고 스크롤
                    const agendaContainer = document.querySelector('.rbc-agenda-view') as HTMLElement;
                    if (agendaContainer && row) {
                      const containerRect = agendaContainer.getBoundingClientRect();
                      const rowRect = row.getBoundingClientRect();
                      const scrollTop = agendaContainer.scrollTop + (rowRect.top - containerRect.top) - 20; // 20px 여유
                      agendaContainer.scrollTop = Math.max(0, scrollTop);
                    } else {
                      row.scrollIntoView({ behavior: 'auto', block: 'start' });
                    }
                    return;
                  }
                }
              }
            }

            // 오늘 날짜 행을 찾지 못한 경우, agenda 컨테이너를 찾아서 스크롤
            const agendaContainer = document.querySelector('.rbc-agenda-view') as HTMLElement;
            if (agendaContainer && agendaContainer.scrollHeight > 0) {
              const todayDate = today.date();
              const daysInMonth = currentMonth.daysInMonth();
              const scrollRatio = todayDate / daysInMonth;
              agendaContainer.scrollTop = agendaContainer.scrollHeight * scrollRatio;
              hasScrolledToTodayRef.current = true;
            }
          }, 100); // DOM 렌더링 대기 시간
        },
        { timeout: 2000 } // 최대 2초 대기
      );
    } else {
      // requestIdleCallback이 없는 경우 setTimeout 사용
      idleId = setTimeout(() => {
        timer = setTimeout(() => {
          const todayPrefix = today.format('MM.DD');
          const dateCells = document.querySelectorAll('.rbc-agenda-date-cell');

          if (dateCells.length === 0) return;

          for (let i = 0; i < dateCells.length; i += 1) {
            const cell = dateCells[i];
            const cellText = cell.textContent?.trim();

            if (cellText && cellText.startsWith(todayPrefix)) {
              const row = cell.closest('tr') as HTMLTableRowElement;
              if (row) {
                const cellDate = dayjs(cellText, 'MM.DD ddd');
                if (cellDate.isSame(today, 'day')) {
                  hasScrolledToTodayRef.current = true;
                  // 상단이 잘리지 않도록 약간의 여유를 두고 스크롤
                  const agendaContainer = document.querySelector('.rbc-agenda-view') as HTMLElement;
                  if (agendaContainer && row) {
                    const containerRect = agendaContainer.getBoundingClientRect();
                    const rowRect = row.getBoundingClientRect();
                    const scrollTop = agendaContainer.scrollTop + (rowRect.top - containerRect.top) - 20; // 20px 여유
                    agendaContainer.scrollTop = Math.max(0, scrollTop);
                  } else {
                    row.scrollIntoView({ behavior: 'auto', block: 'start' });
                  }
                  return;
                }
              }
            }
          }

          const agendaContainer = document.querySelector('.rbc-agenda-view') as HTMLElement;
          if (agendaContainer && agendaContainer.scrollHeight > 0) {
            const todayDate = today.date();
            const daysInMonth = currentMonth.daysInMonth();
            const scrollRatio = todayDate / daysInMonth;
            agendaContainer.scrollTop = agendaContainer.scrollHeight * scrollRatio;
            hasScrolledToTodayRef.current = true;
          }
        }, 100);
      }, 200);
    }

    return () => {
      if (idleId !== null) {
        if (window.cancelIdleCallback && typeof idleId === 'number') {
          window.cancelIdleCallback(idleId);
        } else {
          clearTimeout(idleId as NodeJS.Timeout);
        }
      }
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [currentView, currentDate]);

  const getReq = (reqData: any) => {
    const formData = new FormData();
    Object.keys(reqData).forEach((key) => {
      key === 'users'
        ? reqData[key].forEach((user: string, i: number) => {
            formData.append(`users[${i}]`, user);
          })
        : key === 'files'
        ? reqData[key].forEach((file: any) => {
            formData.append(key, file);
          })
        : key === 'deleteFileIndex'
        ? reqData[key].forEach((id: any, i: number) => {
            formData.append(`deleteFileIndex[${i}]`, id);
          })
        : formData.append(key, reqData[key]);
    });
    return formData;
  };

  const convertToTimeObject = (date: Date) => {
    return {
      h: date.getHours(),
      m: date.getMinutes(),
      s: date.getSeconds(),
    };
  };

  const openScheduleAddModal = useCallback(
    (slotInfo: SlotInfo) => {
      const sDate = new Date(slotInfo.start);
      const eDate = new Date(slotInfo.slots[slotInfo.slots.length - 1]);

      const startSecond = sDate.setMinutes(sDate.getMinutes());
      const endSecond = eDate.setMinutes(eDate.getMinutes());
      const startDateFmt = new Date(startSecond);
      const endDateFmt = new Date(endSecond);
      const startFmt = dayjs(slotInfo.start).format('YYYYMMDD');
      const dateSlot = slotInfo.slots.length === 1 ? { start: startFmt, end: startFmt } : { start: startFmt, end: dayjs(endDateFmt).format('YYYYMMDD') };
      const rangeSlot = slotInfo.slots.length === 1 ? { startDate: slotInfo.start, endDate: startDateFmt } : { startDate: slotInfo.start, endDate: endDateFmt };
      const { h: startH, m: startM } = convertToTimeObject(rangeSlot.startDate);
      const { h: endH, m: endM } = convertToTimeObject(rangeSlot.endDate);
      const timeStart = startH + startM;
      const timeEnd = endH + endM;

      initSet();
      setIsSaveClicked(false);
      setIsNewSchedule(true);
      setSiteManagerName('');
      setSiteManagerPhone('');
      setSiteManagerPartname('');
      setDateTime(dateSlot);
      setRangeState([{ key: 'selection', ...rangeSlot }]);

      if (currentView !== 'month' && timeStart !== timeEnd) {
        setSelectedTimeStart(convertToTimeObject(rangeSlot.startDate));
        setSelectedTimeEnd(convertToTimeObject(rangeSlot.endDate));
        setSelectEvent((prev: any) => ({ ...prev, alldayYn: 'N' })); // 직접 시간 지정 등록 시 종일 체크 해제
      }
      setOpenModal((prev: IModal) => ({ ...prev, type: 'schedule', status: true }));
    },
    [currentView, initSet]
  );

  // 일자 - 슬롯 선택 이벤트
  const onSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      if (mobile && slotInfo.slots.length === 1) {
        const selectedEvents = myEvents.filter((el: Event) => {
          const eventStart = dayjs(el.start).startOf('day');
          const eventEnd = dayjs(el.end).startOf('day');
          const selectedDate = dayjs(slotInfo.start).startOf('day');

          return selectedDate.isSameOrAfter(eventStart, 'day') && selectedDate.isSameOrBefore(eventEnd, 'day');
        });

        // 일정이 없는 경우 일정추가 모달로 연결
        if (selectedEvents.length === 0) {
          if (auth.createAuth) openScheduleAddModal(slotInfo);
          return;
        }

        // 팝업 열기 전 터치 비활성화
        setIsPopupInteractive(false);

        // 팝업을 초기 상태로 설정 (isOpening: true로 시작)
        setPopupData({
          isOpen: true,
          isOpening: true,
          date: slotInfo.start,
          events: selectedEvents,
          position: null,
        });

        // 즉시 isOpening을 false로 설정하여 팝업이 빠르게 표시되도록 함
        // requestAnimationFrame을 사용하여 다음 프레임에서 상태 업데이트
        requestAnimationFrame(() => {
          setPopupData((prev) => (prev ? { ...prev, isOpening: false } : null));
        });

        // 아이폰 터치 이벤트 전파 방지를 위해 300ms 후 터치 활성화
        setTimeout(() => {
          setIsPopupInteractive(true);
        }, 300);
        return;
      }

      if (isSlot && auth.createAuth) {
        openScheduleAddModal(slotInfo);
      }
    },
    [myEvents, isSlot, openScheduleAddModal, setPopupData]
  );

  // 일정 폰트/배경색상 설정 - useCallback으로 메모이제이션
  const eventPropGetter = useCallback<EventPropGetter<Event>>(
    (event) => {
      const backgroundColor = currentView === Views.AGENDA ? 'transparent' : (event as Event)?.backColor; // agenda 뷰에서는 배경색 제거
      const ftColor = (event as Event)?.fontColor; // 기본 글자색

      // 월별 뷰에서 일정 기간이 모두 현재 달 밖(이전/다음달 미리보기 날짜)이면 흐리게
      const isOffRangeEvent =
        currentView === Views.MONTH &&
        !dayjs(event.start).isSame(currentDate, 'month') &&
        !dayjs(event.end).isSame(currentDate, 'month') &&
        !(dayjs(event.start).isBefore(currentDate, 'month') && dayjs(event.end).isAfter(currentDate, 'month'));

      return {
        style: {
          backgroundColor,
          color: ftColor,
          borderRadius: '3px',
          border: 'none',
          ...(isOffRangeEvent && { opacity: 0.4 }),
          ...(mobile &&
            currentView !== Views.AGENDA && {
              pointerEvents: 'none',
              touchAction: 'manipulation',
            }), // 모바일에서는 일정 클릭이벤트 비활성화 (agenda view 제외)
        },
        className: event.smGubun === '4' ? 'event-fixed' : '',
      };
    },
    [mobile, currentView, currentDate]
  );

  const onView = useCallback((newView: CalendarView) => {
    setCurrentView(newView);
    // localStorage에 뷰 저장
    if (typeof window !== 'undefined') {
      localStorage.setItem('calendar-view', newView);
    }
    // 모든 view로 전환 시 현재 보고 있는 날짜 유지
    // AGENDA 뷰는 agendaDate가 월의 1일로 설정되어 전체 월을 표시하므로 currentDate는 유지
  }, []);

  const memoizedNavigate = useCallback(
    (newDate: Date, view: CalendarView, action: NavigateAction) => {
      if (view === Views.AGENDA) {
        // agenda view일 때는 action에 따라 직접 계산
        let targetDate: Date;
        if (action === Navigate.NEXT) {
          // currentDate를 기준으로 다음 달의 1일로 이동
          targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        } else if (action === Navigate.PREVIOUS) {
          // currentDate를 기준으로 이전 달의 1일로 이동
          targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        } else if (action === Navigate.TODAY) {
          // 오늘 버튼 클릭 시
          const today = new Date();
          const todayYear = today.getFullYear();
          const todayMonth = today.getMonth();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth();

          // 현재 월이 오늘이 속한 월과 같으면 스크롤만 이동 (currentDate 변경 안 함)
          if (currentYear === todayYear && currentMonth === todayMonth) {
            // 스크롤만 이동
            setTimeout(() => {
              scrollToToday();
            }, 100);
            return; // currentDate 변경하지 않음
          }

          // 다른 월이면 해당 월의 1일로 이동
          targetDate = new Date(todayYear, todayMonth, 1);
        } else {
          // 기타 경우 (Navigate.DATE 등)에는 newDate의 해당 월 1일로 설정
          targetDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
        }
        setCurrentDate(targetDate);
      } else {
        // 다른 view일 때는 newDate를 그대로 사용
        setCurrentDate(newDate);
      }
    },
    [currentDate, setCurrentDate, scrollToToday]
  );
  const memoizedDraggableAccessor = useCallback(
    (event: Event) => {
      const { complete, isDayOffEvent, isRepeatSchedule } = event;
      if (!auth.updateAuth || complete || isDayOffEvent || isRepeatSchedule) return false;
      return true;
    },
    [auth.updateAuth]
  );
  const memoizedEventDrop = useCallback((args: any) => moveEvent(args), [moveEvent]);
  const memoizedDragStart = useCallback(
    (args: any) => {
      const { event } = args;

      // 성능 최적화: 드래그 시작 시 원본 일정의 너비를 한 번만 측정하여 캐싱
      let cachedOriginalWidth = 0;
      // 초기화 완료 플래그
      let isInitialized = false;
      // 드래그 진행 여부
      let isDragging = false;
      // 현재 사용 중인 프리뷰 DOM (react-big-calendar가 프리뷰를 교체할 때 재적용용)
      let currentDragPreview: HTMLElement | null = null;

      // 드래그 프리뷰가 생성될 때까지 대기 후 내용 복제
      const initDragPreview = () => {
        if (!isDragging) return;
        // 이미 초기화되었으면 중단
        if (isInitialized) {
          return;
        }

        const dragPreviews = document.querySelectorAll('.rbc-addons-dnd-drag-preview');

        // 중복 프리뷰 제거 (첫 번째 것만 남기고 나머지 제거)
        if (dragPreviews.length > 1) {
          for (let i = 1; i < dragPreviews.length; i += 1) {
            dragPreviews[i].remove();
          }
        }

        const dragPreview = dragPreviews[0] as HTMLElement;
        if (dragPreview) {
          currentDragPreview = dragPreview;
          // 원본 이벤트 요소 찾기 - 식별자 기반으로 탐색
          const eventId = event.smIdx || event.id;
          let originalEvent: HTMLElement | null = null;

          if (eventId) {
            const candidate = document.querySelector(`[data-event-id="${eventId}"]`) as HTMLElement | null;
            if (candidate && !candidate.classList.contains('rbc-addons-dnd-drag-preview')) {
              originalEvent = candidate;
            }
          }

          if (originalEvent) {
            // 원본 이벤트의 실제 렌더링된 너비를 한 번만 측정하여 캐싱 (드래그 프리뷰 내용과 관계없이)
            if (cachedOriginalWidth === 0) {
              const originalRect = (originalEvent as HTMLElement).getBoundingClientRect();
              cachedOriginalWidth = originalRect.width;
            }

            const originalContent = (originalEvent as HTMLElement).querySelector('.custom-event-line') || (originalEvent as HTMLElement);
            // 드래그 프리뷰에 내용 복제
            const isEmpty = dragPreview.innerHTML.trim() === '';
            const hasCustomEventLine = !dragPreview.querySelector('.custom-event-line');
            if (isEmpty || hasCustomEventLine) {
              const clonedContent = originalContent.cloneNode(true) as HTMLElement;
              dragPreview.innerHTML = '';
              dragPreview.appendChild(clonedContent);
            }

            // 캐시된 원본 너비 사용 (항상 적용)
            const finalWidth = cachedOriginalWidth;

            // 스타일 적용 - 일정 기간에 비례한 너비 강제 적용 (cssText로 한 번에 설정)
            dragPreview.style.cssText = `
              z-index: 9999 !important;
              position: fixed !important;
              transform: none;
              width: ${finalWidth}px !important;
              min-width: auto !important;
              max-width: ${finalWidth}px !important;
              flex: none !important;
              flex-grow: 0 !important;
              flex-shrink: 0 !important;
              padding: 4px 8px;
              background-color: ${event.backColor || 'var(--tonal)'};
              color: ${event.fontColor || 'var(--text-primary)'};
              border-radius: 4px;
              pointer-events: none;
            `.trim();

            // 초기화 완료 표시
            isInitialized = true;
          }
        } else if (!isInitialized) {
          // 아직 생성되지 않았다면 다시 시도
          requestAnimationFrame(initDragPreview);
        }
      };

      // 초기화 실행 (드래그 시작 시점)
      isDragging = true;
      requestAnimationFrame(initDragPreview);

      // 이전에 음영이 적용된 날짜 셀 추적
      let previousHighlightedCell: HTMLElement | null = null;
      // 쓰로틀링을 위한 변수
      let lastHighlightTime = 0;
      const HIGHLIGHT_THROTTLE_MS = 50; // 50ms마다 실행
      // 날짜 셀 위치 캐시
      let cachedDayCellRects: { el: HTMLElement; left: number; right: number; top: number; bottom: number }[] = [];

      const buildDayCellRects = () => {
        const cells = document.querySelectorAll('.rbc-day-bg, .rbc-day-slot');
        cachedDayCellRects = [];
        for (let i = 0; i < cells.length; i += 1) {
          const el = cells[i] as HTMLElement;
          const rect = el.getBoundingClientRect();
          cachedDayCellRects.push({ el, left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom });
        }
      };

      // 드래그 중 마우스 위치에 따라 프리뷰 이동
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        if (e.buttons !== 1) return; // 왼쪽 버튼 드래그 아닐 때 무시

        // 중복 프리뷰 제거 (react-big-calendar가 계속 생성할 수 있음)
        const allDragPreviews = document.querySelectorAll('.rbc-addons-dnd-drag-preview');
        if (allDragPreviews.length > 1) {
          for (let i = 1; i < allDragPreviews.length; i += 1) {
            allDragPreviews[i].remove();
          }
        }

        const dragPreview = allDragPreviews[0] as HTMLElement;
        if (!dragPreview) {
          // 프리뷰가 사라진 경우 재초기화 시도
          isInitialized = false;
          currentDragPreview = null;
          requestAnimationFrame(initDragPreview);
          return;
        }

        if (dragPreview) {
          // 드래그 프리뷰를 body에 직접 추가하여 stacking context 문제 해결
          if (dragPreview.parentElement !== document.body) {
            document.body.appendChild(dragPreview);
          }

          // react-big-calendar가 프리뷰 DOM을 교체했을 때 스타일 재적용
          if (dragPreview !== currentDragPreview) {
            currentDragPreview = dragPreview;
            const finalWidth = cachedOriginalWidth > 0 ? cachedOriginalWidth : dragPreview.getBoundingClientRect().width || 0;
            dragPreview.style.cssText = `
              z-index: 9999 !important;
              position: fixed !important;
              transform: none;
              width: ${finalWidth}px !important;
              min-width: auto !important;
              max-width: ${finalWidth}px !important;
              flex: none !important;
              flex-grow: 0 !important;
              flex-shrink: 0 !important;
              padding: 4px 8px;
              background-color: ${event.backColor || 'var(--tonal)'};
              color: ${event.fontColor || 'var(--text-primary)'};
              border-radius: 4px;
              pointer-events: none;
            `.trim();
          }

          // 좌표만 갱신 (base style은 init 시 설정)
          dragPreview.style.left = `${e.clientX}px`;
          dragPreview.style.top = `${e.clientY}px`;

          // 쓰로틀링 적용: 배경 셀 색깔 변경 로직
          const now = Date.now();
          if (now - lastHighlightTime >= HIGHLIGHT_THROTTLE_MS) {
            lastHighlightTime = now;

            if (cachedDayCellRects.length === 0) {
              buildDayCellRects();
            }

            // 마우스 위치 아래의 날짜 셀 찾기 (캐시된 rect 사용)
            let dayCell: HTMLElement | null = null;
            const { clientX, clientY } = e;
            for (let i = 0; i < cachedDayCellRects.length; i += 1) {
              const rect = cachedDayCellRects[i];
              if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
                dayCell = rect.el;
                break;
              }
            }

            // 이전 셀의 음영 제거
            if (previousHighlightedCell && previousHighlightedCell !== dayCell) {
              previousHighlightedCell.classList.remove('drag-over-highlight');
            }

            // 현재 날짜 셀에 음영 추가
            if (dayCell && dayCell !== previousHighlightedCell) {
              dayCell.classList.add('drag-over-highlight');
              previousHighlightedCell = dayCell;
            } else if (!dayCell && previousHighlightedCell) {
              // 마우스가 셀 밖에 있으면 이전 셀의 음영 제거
              previousHighlightedCell.classList.remove('drag-over-highlight');
              previousHighlightedCell = null;
            }
          }
        }
      };

      const handleMouseUp = () => {
        isDragging = false;
        // 드래그 종료 시 이벤트 리스너 제거
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // 드래그 프리뷰 제거
        const dragPreviews = document.querySelectorAll('.rbc-addons-dnd-drag-preview');
        dragPreviews.forEach((preview) => {
          preview.remove();
        });

        // 모든 날짜 셀의 음영 제거
        if (previousHighlightedCell) {
          previousHighlightedCell.classList.remove('drag-over-highlight');
          previousHighlightedCell = null;
        }
        // 모든 날짜 셀에서 음영 클래스 제거 (안전장치)
        document.querySelectorAll('.rbc-day-bg.drag-over-highlight, .rbc-day-slot.drag-over-highlight').forEach((cell) => {
          cell.classList.remove('drag-over-highlight');
        });
      };

      // 마우스 이동 이벤트 리스너 추가
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [currentView]
  );

  const memoizedFormats = useMemo(
    () => ({
      dateFormat: (date: Date) => dayjs(date).format('D'),
      dayFormat: (date: Date) => dayjs(date).format('D (ddd)'),
      agendaDateFormat: (date: Date) => {
        const cultureCode = culture.split('-');
        const isKorean = cultureCode[0] === 'ko';
        if (isKorean) {
          return dayjs(date).format('MM.DD ddd');
        }
        return dayjs(date).locale('en').format('MM.DD ddd');
      },
    }),
    [culture]
  );

  const scrollToTime = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0); // KST 브라우저 기준
    return d;
  }, []);

  const min = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const max = React.useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  // Agenda 뷰에서 표시할 일수 (현재 월의 일수)
  const agendaLength = React.useMemo(() => {
    return dayjs(currentDate).daysInMonth();
  }, [currentDate]);

  const holidayDates = useMemo(() => new Set((events || []).filter((ev: Event) => ev.isHolidayEvent).map((ev: Event) => dayjs(ev.start).format('YYYYMMDD'))), [events]);

  const memoizedCalendar = useMemo(
    (): JSX.Element => (
      <DnDCalendar
        views={{
          [Views.DAY]: true,
          [Views.WEEK]: true,
          [Views.MONTH]: true,
          [Views.AGENDA]: SortedAgendaView,
          ['twoWeeks' as CalendarView]: TwoWeeksMonthView,
          ['threeWeeks' as CalendarView]: ThreeWeeksMonthView,
        }}
        components={components as any}
        culture={culture}
        date={agendaDate}
        defaultDate={defaultDate}
        defaultView={Views.MONTH}
        view={currentView as View}
        getDrilldownView={() => null} // 드릴다운 view를 null로 설정하여 view 전환 방지
        doShowMoreDrillDown={false} // 더보기 클릭 시 드릴다운 비활성화
        onView={(v: CalendarView) => onView(v)}
        localizer={localizer}
        messages={messages}
        events={myEvents}
        draggableAccessor={!mobile ? memoizedDraggableAccessor : () => false}
        onDragStart={memoizedDragStart}
        onEventDrop={memoizedEventDrop}
        // dayLayoutAlgorithm='no-overlap'
        resizable={false}
        selectable
        eventPropGetter={eventPropGetter}
        allDayAccessor='allDay' // allDay를 확인
        startAccessor='start' // 시작 시간
        endAccessor='end' // 종료 시간
        onSelectEvent={(event: Event) => {
          // 휴가 구분(공휴일 포함)은 상세 모달이 열리지 않아 isSlot을 되돌릴 계기가 없으므로 슬롯 클릭을 막지 않음
          if (event.smGubun !== '4') setIsSlot(false);
          handleSelectEvent(event); // 일정 상세 모달 열기
        }} // 일정 항목 클릭 이벤트
        onSelectSlot={onSelectSlot} // 날짜슬롯 클릭 이벤트
        onShowMore={!mobile ? handleShowMore : () => {}}
        onNavigate={memoizedNavigate}
        formats={memoizedFormats}
        length={agendaLength} // Agenda 뷰: 현재 월의 일수만큼 표시
        longPressThreshold={mobile ? 0 : 250} // 모바일에서는 즉시 터치 이벤트 발생
        scrollToTime={scrollToTime}
        min={min}
        max={max}
      />
    ),
    [
      myEvents,
      currentView,
      culture,
      components,
      defaultDate,
      agendaDate,
      handleShowMore,
      memoizedDragStart,
      memoizedEventDrop,
      memoizedFormats,
      memoizedNavigate,
      messages,
      onSelectSlot,
      onView,
      scrollToTime,
      min,
      max,
      eventPropGetter,
      agendaLength,
    ]
  );

  return (
    <CalendarStyle ref={calendarWrapperRef} className={`myCustomHeight ${hideOptionalToolBar ? 'dashboardStyle' : ''} ${isLoading ? 'loading' : ''}`}>
      <HolidayDatesContext.Provider value={holidayDates}>{memoizedCalendar}</HolidayDatesContext.Provider>
      {/** 일정 더보기 팝업 */}
      {popupData?.isOpen && (
        <>
          <OverlayStyle
            className={`schedule-overlay ${!popupData?.isOpening && !popupData?.isClosing ? 'active' : ''}`}
            onClick={onBackPrevPopup}
            onTouchStart={(e) => {
              // 터치가 비활성화된 상태에서는 이벤트 전파 방지 (아이폰 터치 이벤트 버블링 방지)
              if (!isPopupInteractive) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
          <ScheduleShowMorePopupStyle
            className={`schedule-show-more-popup ${!popupData?.isOpening && !popupData?.isClosing ? 'active' : ''} ${popupSlide ? `nav-${popupSlide}` : ''}`}
            x={popupData.position?.x || 0}
            y={popupData.position?.y || 0}
            position={popupData.position?.position || 'bottom'}
            arrowOffset={popupData.position?.arrowOffset || 0}
            onTouchStart={(e) => {
              // 터치가 비활성화된 상태에서는 이벤트 전파 방지 (아이폰 터치 이벤트 버블링 방지)
              if (!isPopupInteractive) {
                e.stopPropagation();
              }
            }}
          >
            <div className='popup-header'>
              <h3 className='flex-center'>
                <span
                  className='popup-date-text'
                  style={{
                    color:
                      holidayDates.has(dayjs(popupData.date).format('YYYYMMDD')) || dayjs(popupData.date).day() === 0 ? 'var(--red)' : dayjs(popupData.date).day() === 6 ? 'var(--blue)' : undefined,
                  }}
                >
                  {dayjs(popupData.date).format('M.D ddd')}
                </span>
                <SmallTextLabel text={`${popupData.events?.length || 0}`} color='blue' size='small' />
              </h3>
              <button type='button' className='close-button' onClick={onBackPrevPopup} aria-label='닫기'>
                <span className='material-symbols-rounded'>close</span>
              </button>
            </div>
            <div className='popup-content-wrapper'>
              {/* 모바일: 하루 단위 네비게이션 화살표 */}
              {mobile && (
                <button
                  type='button'
                  className='nav-arrow left'
                  onClick={() => navigatePopupDay(-1)}
                  onTouchStart={(e) => {
                    if (!isPopupInteractive) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  aria-label='이전 날짜'
                >
                  <span className='material-symbols-rounded'>chevron_left</span>
                </button>
              )}
              <div className={`popup-content ${mobile ? 'mobile-popup-content' : ''}`}>
                <ul>
                  {popupData.events?.map((event: Event) => (
                    <li
                      role='presentation'
                      key={event.id}
                      onClick={() => {
                        // 모바일에서만 isPopupInteractive 체크, 데스크탑에서는 항상 클릭 가능
                        if (!mobile || isPopupInteractive) {
                          handleSelectEvent(event);
                        }
                      }}
                      onTouchStart={(e) => {
                        // 모바일에서만 터치 이벤트 전파 방지
                        if (mobile && !isPopupInteractive) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      className={`${mobile ? 'mobile-schedule-item' : ''} ${event.complete ? 'completed' : ''} ${!mobile && event.backColor === '#f3f4f6' ? 'normal' : ''}`}
                      style={!mobile ? { color: event.fontColor, backgroundColor: event.backColor } : {}}
                      title={event.title}
                    >
                      {!mobile ? (
                        <>
                          {event.important && <span className='material-symbols-rounded important-star'>star_rate</span>}
                          {event.bulletColor && <div className='bullet' style={{ backgroundColor: event.bulletColor }} />}
                          <span className='event-title'>{event.title}</span>
                        </>
                      ) : (
                        <>
                          {/* 개인 근태는 대시보드 주간 달력과 같이 근태 바탕색(회색)으로 표시 */}
                          <div className='schedule-type-bg' style={{ backgroundColor: isPersonalDayOff(event) ? 'var(--tonal)' : event.bulletColor || event.backColor }} />
                          <div className='schedule-info-content'>
                            <div className='schedule-info-top'>
                              <span className='event-title' style={event.isHolidayEvent ? { color: 'var(--red)' } : undefined}>
                                {event.title}
                              </span>
                              {(event.important || event.userId.length > 0) && (
                                <div className='schedule-info-attenders'>
                                  {event.important && <span className='material-symbols-rounded important-star'>star_rate</span>}
                                  {event.userId.length > 0 && <span className='attenders-text'>{event.userId.map((el: any) => el.userName).join(', ')}</span>}
                                </div>
                              )}
                            </div>
                            {event.files.length > 0 && (
                              <div className='image-list-wrap'>
                                <ImagePreview imageList={event.files.filter((file: any) => isImageFile(file.fUrl))}>
                                  {event.files
                                    .filter((file: any) => isImageFile(file.fUrl))
                                    .map((file: any) => (
                                      <ThumbnailImage key={file.fIndex} file={file} />
                                    ))}
                                </ImagePreview>
                              </div>
                            )}

                            {/* {event.files.length > 0 && (
                              <div className='schedule-info-files'>
                                {event.files
                                  .filter((file: any) => isImageFile(file.fUrl))
                                  .map((file: any) => (
                                    <figure
                                      role='presentation'
                                      key={file.fileId}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setOpenModal({ status: true, type: 'thumbnail', data: { aImgPath: file.fUrl, fileName: file.fileName, fileFormat: file.fileFormat }, thumbnailOnly: true });
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                      }}
                                    >
                                      <img src={file.fUrl} alt={file.fileName} width={48} height={48} />
                                    </figure>
                                  ))}
                              </div>
                            )} */}
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              {mobile && (
                <button
                  type='button'
                  className='nav-arrow right'
                  onClick={() => navigatePopupDay(1)}
                  onTouchStart={(e) => {
                    if (!isPopupInteractive) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  aria-label='다음 날짜'
                >
                  <span className='material-symbols-rounded'>chevron_right</span>
                </button>
              )}
            </div>
          </ScheduleShowMorePopupStyle>
        </>
      )}

      <Portal openModal={openModal?.status}>
        {openModal && openModal.type === 'delete' && <div />}
        {openModal && openModal.type === 'confirm' && <ConfirmModal openModal={openModal} setOpenModal={setOpenModal} text={openModal.text} />}

        {/* {openModal && openModal.type === 'thumbnail' && <Thumbnail openModal={openModal} setOpenModal={setOpenModal} download />} */}
        {openModal && openModal.type === 'schedule' && (
          <ScheduleSaveModal
            auth={auth}
            openModal={openModal}
            setOpenModal={setOpenModal}
            title={isNewSchedule ? '일정 항목 등록' : '일정 항목'}
            isNewSchedule={isNewSchedule}
            data={selectEvent}
            initScheduleInfoData={initScheduleInfoData}
            setData={(data: any) => setSelectEvent(data as any)}
            address={address}
            setAddress={setAddress}
            deleteAPI={deleteAPI}
            onClickSave={onClickAddSchedule}
            // onClickUpdate={updateEventMutation}
            dateTime={dateTime}
            setDateTime={setDateTime}
            rangeState={rangeState}
            setRangeState={setRangeState}
            selectedTimeStart={selectedTimeStart}
            setSelectedTimeStart={setSelectedTimeStart}
            selectedTimeEnd={selectedTimeEnd}
            setSelectedTimeEnd={setSelectedTimeEnd}
            availableTime={availableTime}
            availableTimeRange={availableTimeRange}
            setAvailableTimeRange={setAvailableTimeRange}
            csCd={csCd}
            setCsCd={setCsCd}
            ccCd={ccCd}
            setCcCd={setCcCd}
            dCd={dCd}
            setDCd={setDCd}
            hsCd1={hsCd1}
            setHsCd1={setHsCd1}
            hsCd2={hsCd2}
            setHsCd2={setHsCd2}
            wGubun={wGubun}
            setWGubun={setWGubun}
            users={users}
            setUsers={setUsers}
            smNameRef={smNameRef}
            siteManagerNameRef={siteManagerNameRef}
            siteManagerPhoneRef={siteManagerPhoneRef}
            // contentsRef={contentsRef}
            isSaveClicked={isSaveClicked}
            isEditable={isEditable}
            setIsSlot={setIsSlot}
            headInfoList={headInfoList}
            scheduleInfoList={scheduleInfoList}
            userPartList={userPartList}
            carInfoList={carInfoList}
            companyUser={companyUser}
            isPaid={isPaid}
            setIsPaid={setIsPaid}
            modalOrigin='calendar'
            siteManagerName={siteManagerName}
            setSiteManagerName={setSiteManagerName}
            siteManagerPhone={siteManagerPhone}
            setSiteManagerPhone={setSiteManagerPhone}
            siteManagerPartname={siteManagerPartname}
            setSiteManagerPartname={setSiteManagerPartname}
            isSaving={isSaving}
          />
        )}
      </Portal>
    </CalendarStyle>
  );
};

export default BigCalendar;
